import "./ResumeForm.css";

import { zodResolver } from "@hookform/resolvers/zod";
import { msg, t } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { createId } from "@paralleldrive/cuid2";
import {
  ArrowRight,
  Briefcase,
  Browsers,
  CaretDown,
  ChartBar,
  ChartLineUp,
  ChatCircle,
  Check,
  CircleNotch,
  CloudArrowUp,
  Download,
  File,
  FileArrowUp,
  FileText,
  Funnel,
  Link as LinkIcon,
  LinkSimple,
  MagnifyingGlass,
  Plus,
  Sparkle,
  Star,
  TextAlignLeft,
  Upload,
  X,
} from "@phosphor-icons/react";
import { TOKEN_COSTS } from "@stylingcv/config";
import { getStreamingGenerationCost } from "@/client/services/token/token-config.service";
import { PDFParser } from "@stylingcv/parser";
import { cn } from "@stylingcv/utils";
import {
  Badge,
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
  ScrollArea,
  Separator,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  Tooltip,
  TooltipProvider,
} from "@stylingcv/ui";
import axios from "axios"; // Added axios import
// @ts-expect-error: No types for country-list
import { getCodes, getName } from "country-list";
import { AnimatePresence, motion } from "framer-motion";
import React, { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { z } from "zod";

import { useDynamicCountryDetection } from "@/client/hooks/use-dynamic-country-detection";
import { useRTL } from "@/client/hooks/use-rtl";
import { trackExtractJobUrl } from "@/client/utils/gtm";
import { useGulfCountryDetection } from "@/client/utils/gulf-countries-detector";

import { ENV_TOKEN_COSTS } from "@/client/config/env-token-config";
import {
  isInsufficientTokensMessage,
  parseNestErrorMessage,
  parseRequiredTokensFromMessage,
} from "@/client/utils/parse-ai-gateway-error";

import { useTokenModal } from "../../../contexts/token-modal-context";
import { useAuthStore } from "../../../stores/auth";
import { useTokenStore } from "../../../stores/token";
import { NextStepsModal } from "@/client/pages/smart-generator/components/next-steps-modal";
import { toast } from "@/client/hooks/use-toast";

// Creation mode type
type CreationMode = "form" | "quickstart" | "upload" | "voice";

// URL detection patterns for quick start
const URL_PATTERN = /^https?:\/\//i;
const JOB_SITE_PATTERNS = [
  /linkedin\.com\/jobs/i,
  /indeed\.com/i,
  /glassdoor\.com/i,
  /monster\.com/i,
  /ziprecruiter\.com/i,
  /careers\./i,
  /jobs\./i,
];

// Form validation schema - error message passed from component for i18n
const createFormSchema = (jobRoleRequiredMessage: string) =>
  z.object({
    jobRole: z.string().min(1, jobRoleRequiredMessage),
    language: z.string(),
    country: z.string(),
    experience: z.string(),
    tone: z.string(),
    atsTargetMin: z.number().min(90).max(100).optional(),
    additionalDescription: z.string().optional(),
    jobUrl: z.string().optional(),
  });

type FormValues = z.infer<ReturnType<typeof createFormSchema>>;

/** Initial form data for intake from landing pages */
export type InitialFormData = {
  jobRole?: string;
  language?: string;
  country?: string;
  experience?: string;
  tone?: string;
  atsTargetMin?: number;
  additionalDescription?: string;
  showAdditionalDescription?: boolean;
  jobUrl?: string;
};

type ResumeFormProps = {
  onSubmit: (data: FormValues) => void;
  isLoading?: boolean;
  tokenBalance?: number;
  isTokenLoading?: boolean;
  onRefreshTokens?: () => void;
  onFormFocus?: () => void;
  initialJobRole?: string;
  /** Initial form data from intake token (overrides initialJobRole) */
  initialFormData?: InitialFormData;
  /** When provided, Upload tab calls this instead of showing inline dropzone (e.g. open parent modal) */
  onUploadTabClick?: () => void;
  /** Wraps the main form body in a premium glassmorphism container */
  glassmorphism?: boolean;
};

// Language options with flags - moved inside component to fix translation hashing

const LANGUAGE_MAP: Record<string, string> = {
  EN: "English",
  AR: "العربية",
  FR: "Français",
  DE: "Deutsch",
  ES: "Español",
  IT: "Italiano",
  PT: "Português",
  NL: "Nederlands",
  RU: "Русский",
  ZH: "中文",
  JA: "日本語",
  KO: "한국어",
  HI: "हिन्दी",
  TR: "Türkçe",
};

// Map language codes to proper locale codes for resume metadata
const LANGUAGE_TO_LOCALE_MAP: Record<string, string> = {
  EN: "en-US",
  AR: "ar-SA",
  FR: "fr-FR",
  DE: "de-DE",
  ES: "es-ES",
  IT: "it-IT",
  PT: "pt-PT",
  NL: "nl-NL",
  RU: "ru-RU",
  ZH: "zh-CN",
  JA: "ja-JP",
  KO: "ko-KR",
  HI: "hi-IN",
  TR: "tr-TR",
};

const LANGUAGE_FLAGS: Record<string, string> = {
  EN: "us",
  AR: "sa",
  FR: "fr",
  DE: "de",
  ES: "es",
  IT: "it",
  PT: "pt",
  NL: "nl",
  RU: "ru",
  ZH: "cn",
  JA: "jp",
  KO: "kr",
  HI: "in",
  TR: "tr",
};

const EXPERIENCE_LEVELS = ["0-1", "2-4", "5-7", "8-10", "10+"];

// Tone values for form/API; labels translated inside component
const TONE_OPTIONS = ["Formal", "Professional", "Friendly", "Creative", "Technical"];

// Convert country code to flag emoji
const getCountryFlag = (countryCode: string): string => {
  const code = countryCode.toUpperCase();
  return [...code]
    .map((char) => String.fromCodePoint(0x1_f1_e6 + char.charCodeAt(0) - 65))
    .join("");
};

// Get language flag emoji from language flag mapping
const getLanguageFlag = (langCode: string): string => {
  const flagCode = LANGUAGE_FLAGS[langCode];
  return flagCode ? getCountryFlag(flagCode) : "🌐";
};

// Hero variant constants (exact original design) - labels are native, no translation needed
const HERO_LANGUAGES = [
  { value: "EN", label: "English", flag: "🇺🇸" },
  { value: "AR", label: "العربية", flag: "🇸🇦" },
  { value: "FR", label: "Français", flag: "🇫🇷" },
  { value: "DE", label: "Deutsch", flag: "🇩🇪" },
  { value: "ES", label: "Español", flag: "🇪🇸" },
  { value: "IT", label: "Italiano", flag: "🇮🇹" },
  { value: "PT", label: "Português", flag: "🇵🇹" },
  { value: "NL", label: "Nederlands", flag: "🇳🇱" },
  { value: "RU", label: "Русский", flag: "🇷🇺" },
  { value: "ZH", label: "中文", flag: "🇨🇳" },
  { value: "JA", label: "日本語", flag: "🇯🇵" },
  { value: "KO", label: "한국어", flag: "🇰🇷" },
  { value: "HI", label: "हिन्दी", flag: "🇮🇳" },
  { value: "TR", label: "Türkçe", flag: "🇹🇷" },
];

export const ResumeForm = ({
  onSubmit,
  isLoading = false,
  tokenBalance = 0,
  isTokenLoading = false,
  onRefreshTokens,
  onFormFocus,
  initialJobRole = "",
  initialFormData,
  onUploadTabClick,
  glassmorphism = false,
}: ResumeFormProps) => {
  const navigate = useNavigate();
  const jobRoleInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const heroTextareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [heroInputFocused, setHeroInputFocused] = useState(false);
  const subscriptionTier = useAuthStore((state) => state.subscriptionTier);
  const isPro = subscriptionTier === "PROFESSIONAL" || subscriptionTier === "ENTERPRISE";
  const [heroLanguageOpen, setHeroLanguageOpen] = useState(false);
  const [languagePillOpen, setLanguagePillOpen] = useState(false);
  const [showJobUrlInput, setShowJobUrlInput] = useState(false);
  const [showJobDescriptionInput, setShowJobDescriptionInput] = useState(false);
  const [jobPreview, setJobPreview] = useState<Record<string, string> | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [plusMenuOpen, setPlusMenuOpen] = useState(false);

  // Hero countries list
  const heroCountries = React.useMemo(
    () =>
      getCodes()
        .map((code: string) => ({ code, name: getName(code) || code }))
        .sort((a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name)),
    [],
  );

  // Creation mode state
  const [creationMode, setCreationMode] = useState<CreationMode>("form");

  // Upload state
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [showPostUploadModal, setShowPostUploadModal] = useState(false);
  const [uploadedResumeData, setUploadedResumeData] = useState<any>(null);

  // Quick start state
  const [quickStartValue, setQuickStartValue] = useState("");
  const [isQuickStartLoading, setIsQuickStartLoading] = useState(false);
  const [isQuickStartUrl, setIsQuickStartUrl] = useState(false);
  const quickStartInputRef = useRef<HTMLInputElement>(null);

  // Mad-lib new state
  const [targetIndustry, setTargetIndustry] = useState("");
  const [vibe, setVibe] = useState("");
  const [pageCount, setPageCount] = useState<"1" | "2" | "auto">("auto");
  const [openBlank, setOpenBlank] = useState<
    "role" | "years" | "industry" | "location" | "tone" | "vibe" | null
  >(null);
  const blankContainerRef = React.useRef<HTMLDivElement>(null);
  const [roleCustomInput, setRoleCustomInput] = useState("");
  const [langMoreOpen, setLangMoreOpen] = useState(false);

  // Detect if quick start input is a URL
  const detectQuickStartUrl = React.useCallback((input: string) => {
    const trimmed = input.trim();
    return URL_PATTERN.test(trimmed) || JOB_SITE_PATTERNS.some((p) => p.test(trimmed));
  }, []);

  const handleQuickStartChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setQuickStartValue(newValue);
    setIsQuickStartUrl(detectQuickStartUrl(newValue));
  };

  const handleQuickStartSubmit = async () => {
    const trimmed = quickStartValue.trim();
    if (!trimmed) return;

    // Get current form values for defaults
    const currentLanguage = watch("language") || "EN";
    const currentCountry = watch("country") || "US";
    const currentExperience = watch("experience") || "2-4";
    const currentTone = watch("tone") || "Professional";

    if (isQuickStartUrl) {
      setIsQuickStartLoading(true);
      try {
        const response = await axios.post("/api/resume/scrape-job-url", { url: trimmed });
        const data = response.data?.data || response.data;

        // Directly submit with extracted data
        const jobRole = data.jobRole || data.title || trimmed;
        const description = data.description || "";

        onSubmit({
          jobRole,
          language: currentLanguage,
          country: currentCountry,
          experience: currentExperience,
          tone: currentTone,
          additionalDescription: description,
          jobUrl: trimmed,
        });
        setQuickStartValue("");
      } catch {
        // On error, just use the URL as job role and submit
        onSubmit({
          jobRole: trimmed,
          language: currentLanguage,
          country: currentCountry,
          experience: currentExperience,
          tone: currentTone,
          additionalDescription: "",
          jobUrl: trimmed,
        });
        setQuickStartValue("");
      } finally {
        setIsQuickStartLoading(false);
      }
    } else {
      // Just submit with the job role directly
      onSubmit({
        jobRole: trimmed,
        language: currentLanguage,
        country: currentCountry,
        experience: currentExperience,
        tone: currentTone,
        additionalDescription: "",
        jobUrl: "",
      });
      setQuickStartValue("");
    }
  };

  const handleQuickStartKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleQuickStartSubmit();
    }
  };

  // Language options with flags - inside component to fix translation hashing
  const LANGUAGES = [
    { value: "EN", label: "English", flag: "us" },
    { value: "AR", label: "العربية", flag: "sa" },
    { value: "FR", label: "Français", flag: "fr" },
    { value: "DE", label: "Deutsch", flag: "de" },
    { value: "ES", label: "Español", flag: "es" },
    { value: "IT", label: "Italiano", flag: "it" },
    { value: "PT", label: "Português", flag: "pt" },
    { value: "NL", label: "Nederlands", flag: "nl" },
    { value: "RU", label: "Русский", flag: "ru" },
    { value: "ZH", label: "中文", flag: "cn" },
    { value: "JA", label: "日本語", flag: "jp" },
    { value: "KO", label: "한국어", flag: "kr" },
    { value: "HI", label: "हिन्दी", flag: "in" },
    { value: "TR", label: "Türkçe", flag: "tr" },
  ];

  // Token and auth hooks
  const { showTokenModal } = useTokenModal();
  const fetchTokenBalance = useTokenStore((s) => s.fetchTokenBalance);
  const extractPdfCost = ENV_TOKEN_COSTS.EXTRACT_PDF_RESUME;
  const { i18n } = useLingui();
  const isArabicUi = i18n.locale.startsWith("ar");

  // Translated validation message and schema (locale-aware)
  const jobRoleRequiredMessage = i18n._(msg`Job role is required`);
  const formSchema = React.useMemo(
    () => createFormSchema(jobRoleRequiredMessage),
    [jobRoleRequiredMessage],
  );

  // Tone label for display (values stay English for API)
  const getToneLabel = (tone: string) => {
    switch (tone) {
      case "Formal":
        return t`Formal`;
      case "Professional":
        return t`Professional`;
      case "Friendly":
        return t`Friendly`;
      case "Creative":
        return t`Creative`;
      case "Technical":
        return t`Technical`;
      default:
        return tone;
    }
  };

  // Hero tone options with translated labels (recompute on locale change)
  const HERO_TONE_OPTIONS = React.useMemo(
    () => [
      { value: "Professional", label: t`Professional`, short: t`Pro` },
      { value: "Formal", label: t`Formal`, short: t`Formal` },
      { value: "Friendly", label: t`Friendly`, short: t`Friendly` },
      { value: "Creative", label: t`Creative`, short: t`Creative` },
      { value: "Technical", label: t`Technical`, short: t`Tech` },
    ],
    [],
  );

  const jobRolePlaceholder = isArabicUi
    ? "مثال: مدير تسويق يركز على النمو"
    : i18n._(msg`eg. Marketing Manager with growth focus`);
  const resumeLanguageLabel = isArabicUi ? "لغة السيرة الذاتية" : i18n._(msg`Resume Language`);
  const experienceLabel = isArabicUi ? "مستوى الخبرة" : i18n._(msg`Your Experience`);
  const writingToneLabel = isArabicUi ? "نبرة الكتابة" : i18n._(msg`Writing Tone`);
  const buildCtaLabel = isArabicUi
    ? "أنشئ سيرتك الذاتية مجاناً"
    : i18n._(msg`Build Your Resume — FREE`);

  // Hero ATS score options (90-100, Pro only)
  const HERO_ATS_SCORES = React.useMemo(
    () =>
      Array.from({ length: 11 }, (_, i) => {
        const v = 90 + i;
        return { value: v, label: `${v}%`, short: `${v}%` };
      }),
    [],
  );

  // Get the actual streaming generation cost (sum of Market Scout + Generate + Format)
  const STREAMING_COST = getStreamingGenerationCost();

  // RTL language detection and utilities
  const { isRTL, textAlign, getTextAlign, getAlignmentClasses } = useRTL();
  const isLoggedIn = useAuthStore((state) => !!state.user);

  // Country detection
  const { detection: countryDetection, loading: countryLoading } = useGulfCountryDetection();
  const countryData = useDynamicCountryDetection();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      jobRole: initialFormData?.jobRole || initialJobRole,
      language: initialFormData?.language || "EN",
      country: initialFormData?.country || "", // Will be auto-selected based on IP detection if empty
      experience: initialFormData?.experience || "2-4",
      tone: initialFormData?.tone || "Formal",
      atsTargetMin: initialFormData?.atsTargetMin ?? 90,
      additionalDescription: initialFormData?.additionalDescription || "",
      jobUrl: initialFormData?.jobUrl || "",
    },
  });

  const {
    watch,
    handleSubmit,
    setValue,
    formState: { errors },
  } = form;

  // Update jobRole when initialJobRole prop changes
  React.useEffect(() => {
    if (initialJobRole && !initialFormData?.jobRole) {
      setValue("jobRole", initialJobRole);
    }
  }, [initialJobRole, initialFormData?.jobRole, setValue]);

  // Update all form values when initialFormData changes (from intake token)
  React.useEffect(() => {
    if (initialFormData) {
      if (initialFormData.jobRole) setValue("jobRole", initialFormData.jobRole);
      if (initialFormData.language) setValue("language", initialFormData.language);
      if (initialFormData.country) setValue("country", initialFormData.country);
      if (initialFormData.experience) setValue("experience", initialFormData.experience);
      if (initialFormData.tone) setValue("tone", initialFormData.tone);
      if (initialFormData.additionalDescription)
        setValue("additionalDescription", initialFormData.additionalDescription);
      if (initialFormData.showAdditionalDescription) {
        setShowJobDescriptionInput(true);
        setShowJobUrlInput(false);
      }
      if (initialFormData.jobUrl) setValue("jobUrl", initialFormData.jobUrl);
    }
  }, [initialFormData, setValue]);

  // Check if mobile
  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => {
      window.removeEventListener("resize", checkMobile);
    };
  }, []);

  // Auto-select user's country when detected (skip if initialFormData provides country)
  React.useEffect(() => {
    if (countryData.countryCode && !countryData.loading && !initialFormData?.country) {
      setValue("country", countryData.countryCode);
    }
  }, [countryData.countryCode, countryData.loading, initialFormData?.country, setValue]);

  // Auto-select detected country (but not language) - skip if initialFormData provides country
  React.useEffect(() => {
    // Skip auto-detection if initialFormData provides a country
    if (initialFormData?.country) return;

    if (!countryLoading) {
      if (countryDetection) {
        // Map Gulf country codes to standard country codes
        const countryCodeMap: Record<string, string> = {
          JO: "JO", // Jordan
          AE: "AE", // United Arab Emirates
          SA: "SA", // Saudi Arabia
          KW: "KW", // Kuwait
          QA: "QA", // Qatar
          BH: "BH", // Bahrain
          OM: "OM", // Oman
        };

        const detectedCode = countryDetection.countryCode;
        if (detectedCode && countryCodeMap[detectedCode]) {
          setValue("country", countryCodeMap[detectedCode]);
        } else if (countryDetection.countryCode) {
          // For non-Gulf countries, just use the detected code if it exists
          setValue("country", countryDetection.countryCode);
        } else {
          // If no country detected, default to US
          setValue("country", "US");
        }
      } else {
        // If detection failed completely, default to US
        setValue("country", "US");
      }
    }
  }, [countryDetection, countryLoading, initialFormData?.country, setValue]);

  // Click-outside handler to close blank popovers
  React.useEffect(() => {
    if (!openBlank && !langMoreOpen) return;
    const handler = (e: MouseEvent) => {
      const target = e.target;
      const isInsidePopover =
        target instanceof Element && target.closest("[data-resume-form-popover]");

      if (!isInsidePopover) {
        setOpenBlank(null);
        setLangMoreOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [openBlank, langMoreOpen]);

  const toggleHeroDropdown = (dropdown: typeof openBlank) => {
    setLangMoreOpen(false);
    setOpenBlank(openBlank === dropdown ? null : dropdown);
  };

  const toggleHeroLanguageDropdown = () => {
    setOpenBlank(null);
    setLangMoreOpen((isOpen) => !isOpen);
  };

  const handleButtonClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Don't block during loading state
    if (isLoading) {
      return;
    }

    // Check job role
    if (!watch("jobRole")) {
      return;
    }

    // Token check - allow submission, let parent handle insufficient tokens
    // This allows the proper token check flow in the parent component
    // which can fetch fresh balance and show appropriate modals
    const additionalDesc = [
      watch("additionalDescription") || "",
      vibe ? `Vibe: ${vibe}` : "",
      targetIndustry ? `Industry: ${targetIndustry}` : "",
    ]
      .filter(Boolean)
      .join(" | ");

    const formData = {
      jobRole: watch("jobRole"),
      language: watch("language"),
      country: watch("country"),
      experience: watch("experience"),
      tone: watch("tone"),
      atsTargetMin: isPro ? watch("atsTargetMin") : undefined,
      additionalDescription: additionalDesc || undefined,
      jobUrl: watch("jobUrl"),
    };

    onSubmit(formData);
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setValue("additionalDescription", value);
  };

  const handleRefreshTokens = async () => {
    if (onRefreshTokens) {
      onRefreshTokens();
    }
  };

  // Handle PDF upload for "Upload Resume" mode
  const handlePdfUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.type !== "application/pdf") {
      setUploadError(t`Please upload a PDF file.`);
      return;
    }

    setIsUploading(true);
    setUploadedFileName(file.name);
    setUploadError(null);

    try {
      // 1. Extract Text locally
      const parser = new PDFParser();
      const pdfData = await parser.readFile(file);
      const text = pdfData.text;

      console.log("Extracted PDF text length:", text.length);

      // 2. Send to AI Gateway for Structure
      const response = await fetch("/api/ai/resume/parse-pdf", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ pdfText: text, language: watch("language") }),
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        const message = parseNestErrorMessage(payload);

        if (message && isInsufficientTokensMessage(message)) {
          const required = parseRequiredTokensFromMessage(message) ?? extractPdfCost;
          await fetchTokenBalance(isLoggedIn, true);
          const current = useTokenStore.getState().tokenBalance ?? 0;
          showTokenModal({
            requiredTokens: required,
            currentTokens: current,
            featureName: t`PDF resume import`,
          });
          setUploadedFileName(null);
          return;
        }

        if (message.includes("Guest session not found")) {
          setUploadError(t`Your session could not be verified. Refresh the page and try again.`);
          setUploadedFileName(null);
          return;
        }

        setUploadError(message || t`Failed to parse PDF. Please try another file.`);
        setUploadedFileName(null);
        return;
      }

      const structuredData = await response.json();
      console.log("Agentic Parse Complete:", structuredData);

      void fetchTokenBalance(isLoggedIn, true);
      setUploadedResumeData(structuredData);
      setShowPostUploadModal(true);
    } catch (error) {
      console.error("PDF parsing error:", error);
      setUploadError(t`Failed to parse PDF. Please try another file.`);
      setUploadedFileName(null);
    } finally {
      setIsUploading(false);
    }
  };

  /**
   * Ensure resume data matches Stylion schema format with all required fields
   * Handles both Stylion format (with sections) and JSON Resume format (with work, education arrays)
   */
  const ensureStylingCVFormat = (data: any) => {
    const basics = data.basics || {};
    const sections = data.sections || {};

    // Handle location - convert object to string if needed
    let locationString = "";
    if (typeof basics.location === "string") {
      locationString = basics.location;
    } else if (basics.location?.city) {
      locationString = [basics.location.city, basics.location.region, basics.location.country]
        .filter(Boolean)
        .join(", ");
    }

    // Check if data is in JSON Resume format (has work/education arrays at root level)
    const isJSONResumeFormat = data.work || data.education || data.skills;

    // Convert JSON Resume work array to Stylion experience items
    const convertWorkToExperience = (work: any[] = []) => {
      return work.map((job: any) => ({
        id: createId(),
        visible: true,
        company: job.company || "",
        position: job.position || "",
        location: job.location || "",
        date: `${job.startDate || ""} - ${job.endDate || "Present"}`,
        summary: job.summary || "",
        url: { label: "", href: job.url || "" },
      }));
    };

    // Convert JSON Resume education array to Stylion education items
    const convertEducationItems = (education: any[] = []) => {
      return education.map((edu: any) => ({
        id: createId(),
        visible: true,
        institution: edu.institution || "",
        studyType: edu.studyType || "",
        area: edu.area || "",
        score: edu.score || "",
        date: `${edu.startDate || ""} - ${edu.endDate || ""}`,
        summary: edu.summary || "",
        url: { label: "", href: edu.url || "" },
      }));
    };

    // Convert JSON Resume skills array to Stylion skills items
    const convertSkillsItems = (skills: any[] = []) => {
      return skills.map((skill: any) => ({
        id: createId(),
        visible: true,
        name: skill.name || "",
        description: skill.level || "",
        level: 3,
        keywords: skill.keywords || [],
      }));
    };

    // Convert JSON Resume certificates array to Stylion certifications items
    const convertCertificatesItems = (certificates: any[] = []) => {
      return certificates.map((cert: any) => ({
        id: createId(),
        visible: true,
        name: cert.name || "",
        issuer: cert.issuer || "",
        date: cert.date || "",
        summary: cert.summary || "",
        url: { label: "", href: cert.url || "" },
      }));
    };

    // Convert JSON Resume languages array to Stylion languages items
    const convertLanguagesItems = (languages: any[] = []) => {
      return languages.map((lang: any) => ({
        id: createId(),
        visible: true,
        name: lang.language || "",
        description: lang.fluency || "",
        level: 3,
      }));
    };

    // Convert JSON Resume projects array to Stylion projects items
    const convertProjectsItems = (projects: any[] = []) => {
      return projects.map((proj: any) => ({
        id: createId(),
        visible: true,
        name: proj.name || "",
        description: proj.description || "",
        date: proj.date || "",
        summary: proj.summary || "",
        keywords: proj.keywords || [],
        url: { label: "", href: proj.url || "" },
      }));
    };

    // Build experience section - from sections or JSON Resume work array
    const experienceItems =
      isJSONResumeFormat && data.work?.length
        ? convertWorkToExperience(data.work)
        : sections.experience?.items || [];

    // Build education section
    const educationItems =
      isJSONResumeFormat && data.education?.length
        ? convertEducationItems(data.education)
        : sections.education?.items || [];

    // Build skills section
    const skillsItems =
      isJSONResumeFormat && data.skills?.length
        ? convertSkillsItems(data.skills)
        : sections.skills?.items || [];

    // Build certifications section
    const certificationsItems =
      isJSONResumeFormat && data.certificates?.length
        ? convertCertificatesItems(data.certificates)
        : sections.certifications?.items || [];

    // Build languages section
    const languagesItems =
      isJSONResumeFormat && data.languages?.length
        ? convertLanguagesItems(data.languages)
        : sections.languages?.items || [];

    // Build projects section
    const projectsItems =
      isJSONResumeFormat && data.projects?.length
        ? convertProjectsItems(data.projects)
        : sections.projects?.items || [];

    // Get summary content
    const summaryContent = basics.summary || sections.summary?.content || "";

    return {
      basics: {
        name: basics.name || "Your Name",
        headline: basics.headline || basics.label || "",
        email: basics.email || "",
        phone: basics.phone || "",
        location: locationString || "",
        url: basics.url || { label: "", href: "" },
        customFields: basics.customFields || [],
        dateOfBirth: basics.dateOfBirth || "",
        idNumber: basics.idNumber || "",
        nationality: basics.nationality || "",
        maritalStatus: basics.maritalStatus || "",
        picture: basics.picture || {
          url: "",
          size: 64,
          aspectRatio: 1,
          borderRadius: 0,
          effects: { hidden: false, border: false, grayscale: false },
        },
      },
      sections: {
        summary: {
          name: "Summary",
          columns: 1,
          visible: true,
          id: "summary",
          content: summaryContent,
        },
        experience: {
          name: "Experience",
          columns: 1,
          visible: true,
          id: "experience",
          items: experienceItems,
        },
        education: {
          name: "Education",
          columns: 1,
          visible: true,
          id: "education",
          items: educationItems,
        },
        skills: { name: "Skills", columns: 1, visible: true, id: "skills", items: skillsItems },
        languages: {
          name: "Languages",
          columns: 1,
          visible: true,
          id: "languages",
          items: languagesItems,
        },
        certifications: {
          name: "Certifications",
          columns: 1,
          visible: true,
          id: "certifications",
          items: certificationsItems,
        },
        projects: {
          name: "Projects",
          columns: 1,
          visible: true,
          id: "projects",
          items: projectsItems,
        },
        awards: sections.awards || {
          name: "Awards",
          columns: 1,
          visible: true,
          id: "awards",
          items: [],
        },
        interests: sections.interests || {
          name: "Interests",
          columns: 1,
          visible: true,
          id: "interests",
          items: [],
        },
        volunteer: sections.volunteer || {
          name: "Volunteer",
          columns: 1,
          visible: true,
          id: "volunteer",
          items: [],
        },
        references: sections.references || {
          name: "References",
          columns: 1,
          visible: true,
          id: "references",
          items: [],
        },
        profiles: sections.profiles || {
          name: "Profiles",
          columns: 1,
          visible: true,
          id: "profiles",
          items: [],
        },
        publications: sections.publications || {
          name: "Publications",
          columns: 1,
          visible: true,
          id: "publications",
          items: [],
        },
        hobbies: sections.hobbies || {
          name: "Hobbies",
          columns: 1,
          visible: true,
          id: "hobbies",
          items: [],
        },
        signature: sections.signature || {
          name: "Signature",
          columns: 1,
          visible: true,
          id: "signature",
          items: [],
        },
        custom: sections.custom || {},
      },
      metadata: data.metadata || {
        template: "vibrant",
        layout: [
          [
            [
              "summary",
              "experience",
              "education",
              "projects",
              "certifications",
              "awards",
              "mostProudOf",
              "myMind",
            ],
            [
              "skills",
              "languages",
              "profiles",
              "interests",
              "hobbies",
              "volunteer",
              "publications",
              "references",
              "signature",
            ],
          ],
        ],
        css: { value: "", visible: false },
        page: { margin: 18, format: "a4", options: { breakLine: true, pageNumbers: true } },
        typography: {
          font: { family: "Inter", subset: "latin", variants: ["regular"], size: 14 },
          lineHeight: 1.5,
          hideIcons: true,
          underlineLinks: true,
        },
        theme: { background: "#ffffff", text: "#000000", primary: "#0ea5e9" },
        notes: "",
      },
    };
  };

  // Navigate to AI interview - public route for guests
  const handleInterviewClick = () => {
    navigate("/interview");
  };

  // Handle post-upload actions
  const handleStartInterviewFromUpload = () => {
    setShowPostUploadModal(false);
    // Navigate to AI interview with resume data (public route for guests)
    navigate("/interview", {
      state: {
        resumeData: uploadedResumeData,
        resumeText: "",
      },
    });
  };

  const handleSkipToBuilderFromUpload = async () => {
    if (!uploadedResumeData) return;

    setShowPostUploadModal(false);

    try {
      // Transform data to proper Stylion format
      const formattedData = ensureStylingCVFormat(uploadedResumeData);
      console.log("Formatted data for import:", formattedData);

      // Create resume using the import service
      const response = await fetch("/api/resume/import", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ data: formattedData }),
      });

      if (response.ok) {
        const result = await response.json();
        const resumeId = result.id || result.slug;
        if (resumeId) {
          navigate(`/builder/${resumeId}`);
        } else {
          navigate("/dashboard/resumes");
        }
      } else {
        // Try guest import endpoint as fallback
        const guestResponse = await fetch("/api/guest/resume/import", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ data: formattedData }),
        });

        if (guestResponse.ok) {
          const result = await guestResponse.json();
          const resumeId = result.id || result.slug;
          if (resumeId) {
            navigate(`/builder/guest/${resumeId}`);
            return;
          }
        }

        console.error("Failed to import resume, navigating to resumes list");
        navigate("/dashboard/resumes");
      }
    } catch (error) {
      console.error("Error importing resume:", error);
      navigate("/builder/try");
    }
  };

  // Auto-resize textarea
  React.useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const adjustHeight = () => {
      textarea.style.height = "auto"; // Reset height to recalculate
      textarea.style.height = `${Math.min(textarea.scrollHeight, 300)}px`; // Grow up to 300px then scroll
    };

    // Adjust on value change
    adjustHeight();
  }, [watch("additionalDescription")]);

  // Helper function to format job description
  const formatJobDescription = (description: string): string => {
    if (!description) return "";

    // Remove HTML tags and clean up the content
    const cleanDesc = description
      .replace(/<[^>]*>/g, "") // Remove HTML tags
      .replace(/\[([^\]]*)]\([^)]*\)/g, "$1") // Remove markdown links
      .replace(/!\[([^\]]*)]\([^)]*\)/g, "") // Remove images
      .replace(/\n{3,}/g, "\n\n") // Remove excessive line breaks
      .trim();

    // Extract and organize key sections
    const sections: string[] = [];

    // Look for job description section
    const descMatch =
      /(?:job description|description|about the role)[\s:]*([\S\s]*?)(?=\n\s*(?:skills|requirements|qualifications|preferred candidate|apply|$))/i.exec(
        cleanDesc,
      );
    if (descMatch) {
      sections.push(`📋 JOB DESCRIPTION:\n${descMatch[1].trim()}`);
    }

    // Look for skills section
    const skillsMatch =
      /(?:skills|requirements|qualifications)[\s:]*([\S\s]*?)(?=\n\s*(?:preferred candidate|apply|$))/i.exec(
        cleanDesc,
      );
    if (skillsMatch) {
      sections.push(`🛠️ SKILLS & REQUIREMENTS:\n${skillsMatch[1].trim()}`);
    }

    // Look for preferred candidate section
    const candidateMatch =
      /(?:preferred candidate|requirements)[\s:]*([\S\s]*?)(?=\n\s*(?:apply|$))/i.exec(cleanDesc);
    if (candidateMatch) {
      sections.push(`👤 PREFERRED CANDIDATE:\n${candidateMatch[1].trim()}`);
    }

    // If no sections found, use the cleaned content
    if (sections.length === 0) {
      sections.push(`📋 JOB DETAILS:\n${cleanDesc}`);
    }

    return sections.join("\n\n");
  };

  const handlePreviewUrl = async () => {
    const url = watch("jobUrl");
    if (!url || !/^https?:\/\/.+/.test(url)) return;
    setIsPreviewLoading(true);
    setJobPreview(null);
    try {
      const res = await axios.post("/api/resume/scrape-job-url", { url });
      if (res.data.success) {
        const previewData = res.data.data;
        setJobPreview(previewData);

        const extractedRole =
          (typeof previewData?.jobRole === "string" ? previewData.jobRole.trim() : "") ||
          (typeof previewData?.title === "string" ? previewData.title.trim() : "");

        if (extractedRole) {
          setValue("jobRole", extractedRole, { shouldDirty: true, shouldValidate: true });
        }
      }
    } catch (error) {
      const errorData = axios.isAxiosError(error) ? error.response?.data : null;
      const parsedMessage = parseNestErrorMessage(errorData);
      const fallbackMessage = t`Couldn't review this job URL. Please check the link or paste the description manually.`;
      const description = parsedMessage || fallbackMessage;

      toast({
        title: t`URL review failed`,
        description,
        variant: "error",
      });
    } finally {
      setIsPreviewLoading(false);
    }
  };

  // Hero form textarea auto-resize
  React.useEffect(() => {
    const textarea = heroTextareaRef.current;
    if (!textarea) return;
    const adjustHeight = () => {
      textarea.style.height = "auto";
      textarea.style.height = `${Math.min(textarea.scrollHeight, 300)}px`;
    };
    adjustHeight();
  }, [watch("additionalDescription")]);

  // Pill components with magical styling to match form theme ✨
  const PillButton = React.forwardRef<
    HTMLButtonElement,
    {
      children: React.ReactNode;
      icon: React.ReactNode;
      onClick?: () => void;
    }
  >(({ children, icon, onClick, ...props }, ref) => (
    <motion.button
      ref={ref}
      type="button"
      className="ai-pill-button focus:ring-3 group flex h-11 w-full items-center justify-between gap-1.5 rounded-md border border-slate-200 bg-white px-2 text-xs font-medium text-slate-700 shadow-sm transition-all duration-300 hover:border-blue-300 hover:bg-blue-50 focus:border-blue-400 focus:outline-none focus:ring-blue-500/30 dark:border-gray-500 dark:bg-gray-700 dark:text-white dark:shadow-lg dark:hover:border-blue-400 dark:hover:bg-blue-900/40 sm:h-10 sm:px-2.5"
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      {...props}
    >
      <div className="flex items-center gap-1.5">
        <div className="flex size-4 items-center justify-center shadow-sm transition-shadow duration-300 group-hover:shadow-lg">
          {React.cloneElement(icon as React.ReactElement, { className: "w-3 h-3" })}
        </div>
        <span className="truncate font-semibold">{children}</span>
      </div>
      <CaretDown className="size-3.5 text-slate-400 transition-colors duration-300 group-hover:text-blue-500 dark:text-gray-300 dark:group-hover:text-blue-400" />
    </motion.button>
  ));

  PillButton.displayName = "PillButton";

  const LanguagePill = () => (
    <Popover open={languagePillOpen} onOpenChange={setLanguagePillOpen}>
      <PopoverTrigger asChild>
        <PillButton icon={<span className="text-sm">{getLanguageFlag(watch("language"))}</span>}>
          {watch("language")}
        </PillButton>
      </PopoverTrigger>
      <PopoverContent className="ai-dropdown-content w-[95vw] max-w-[95vw] rounded-md border border-slate-200 bg-white p-0 shadow-xl backdrop-blur-xl dark:border-gray-500 dark:bg-gray-700 dark:shadow-2xl sm:w-64 sm:max-w-64 sm:p-0">
        <Command>
          <CommandInput
            placeholder={t`Search language...`}
            className="ai-command-input h-12 text-base sm:h-auto sm:text-sm"
          />
          <CommandList className="ai-command-list language-dropdown-scroll max-h-[280px] overflow-y-auto">
            <CommandEmpty>{t`No language found.`}</CommandEmpty>
            <CommandGroup>
              {LANGUAGES.map((lang) => (
                <CommandItem
                  key={lang.value}
                  value={lang.value}
                  className="ai-command-item sm:min-h-auto flex min-h-[40px] items-center gap-2 p-2 text-base sm:p-1.5 sm:text-sm"
                  onSelect={() => {
                    setValue("language", lang.value);
                    setLanguagePillOpen(false);
                  }}
                >
                  <Check
                    className={`size-4 ${watch("language") === lang.value ? "opacity-100" : "opacity-0"}`}
                  />
                  <span className="text-sm">{getLanguageFlag(lang.value)}</span>
                  {lang.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );

  const ExperiencePill = () => (
    <Popover>
      <PopoverTrigger asChild>
        <PillButton icon={<Briefcase className="size-3" />}>{watch("experience")}</PillButton>
      </PopoverTrigger>
      <PopoverContent className="ai-dropdown-content w-[95vw] max-w-[95vw] rounded-md border border-slate-200 bg-white p-0 shadow-xl backdrop-blur-xl dark:border-gray-500 dark:bg-gray-700 dark:shadow-2xl sm:w-48 sm:max-w-48 sm:p-0">
        <Command>
          <CommandList className="ai-command-list">
            <CommandGroup>
              {EXPERIENCE_LEVELS.map((level) => (
                <CommandItem
                  key={level}
                  value={level}
                  className="ai-command-item sm:min-h-auto flex min-h-[40px] items-center gap-2 p-2 text-base sm:p-1.5 sm:text-sm"
                  onSelect={() => {
                    setValue("experience", level);
                  }}
                >
                  <Check
                    className={`size-4 ${watch("experience") === level ? "opacity-100" : "opacity-0"}`}
                  />
                  {level} {t`years`}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );

  const CountryPill = () => {
    const countries = getCodes()
      .map((code: string) => ({
        code,
        name: getName(code) || code,
      }))
      .sort((a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name));

    const selectedCountry = countries.find((c: { code: string }) => c.code === watch("country"));

    return (
      <Popover>
        <PopoverTrigger asChild>
          <PillButton
            icon={
              <span className="text-sm">
                {getCountryFlag(selectedCountry?.code || watch("country"))}
              </span>
            }
          >
            {selectedCountry?.code || watch("country")}
          </PillButton>
        </PopoverTrigger>
        <PopoverContent className="ai-dropdown-content w-[95vw] max-w-[95vw] rounded-md border border-slate-200 bg-white p-0 shadow-xl backdrop-blur-xl dark:border-gray-500 dark:bg-gray-700 dark:shadow-2xl sm:w-64 sm:max-w-64 sm:p-0">
          <Command>
            <CommandInput
              placeholder={t`Search country...`}
              className="ai-command-input h-12 text-base sm:h-auto sm:text-sm"
            />
            <CommandList className="ai-command-list">
              <CommandEmpty>{t`No country found.`}</CommandEmpty>
              <CommandGroup>
                <ScrollArea className="ai-scroll-area h-32 sm:h-64">
                  {countries.map(({ code, name }: { code: string; name: string }) => (
                    <CommandItem
                      key={code}
                      value={name}
                      className="ai-command-item sm:min-h-auto flex min-h-[40px] items-center gap-2 p-2 text-base sm:p-1.5 sm:text-sm"
                      onSelect={() => {
                        setValue("country", code);
                      }}
                    >
                      <Check
                        className={`size-4 ${watch("country") === code ? "opacity-100" : "opacity-0"}`}
                      />
                      <span className="text-sm">{getCountryFlag(code)}</span>
                      {name}
                    </CommandItem>
                  ))}
                </ScrollArea>
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    );
  };

  const TonePill = () => (
    <Popover>
      <PopoverTrigger asChild>
        <PillButton icon={<ChatCircle className="size-3" />}>
          {getToneLabel(watch("tone"))}
        </PillButton>
      </PopoverTrigger>
      <PopoverContent className="ai-dropdown-content w-[95vw] max-w-[95vw] rounded-md border border-slate-200 bg-white p-0 shadow-xl backdrop-blur-xl dark:border-gray-500 dark:bg-gray-700 dark:shadow-2xl sm:w-48 sm:max-w-48 sm:p-0">
        <Command>
          <CommandList className="ai-command-list">
            <CommandGroup>
              {TONE_OPTIONS.map((tone) => (
                <CommandItem
                  key={tone}
                  value={tone}
                  className="ai-command-item sm:min-h-auto flex min-h-[40px] items-center gap-2 p-2 text-base sm:p-1.5 sm:text-sm"
                  onSelect={() => {
                    setValue("tone", tone);
                  }}
                >
                  <Check
                    className={`size-4 ${watch("tone") === tone ? "opacity-100" : "opacity-0"}`}
                  />
                  {getToneLabel(tone)}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );

  const ATSPill = () => (
    <div className="relative">
      <PillButton icon={<ChartBar className="size-3" />}>
        <span className="flex items-center gap-1">
          <span className="font-bold text-emerald-600 dark:text-emerald-400">85-95%</span>
          <div className="size-1.5 animate-pulse rounded-full bg-emerald-500"></div>
        </span>
      </PillButton>
    </div>
  );

  const FiltersPill = () => (
    <Sheet>
      <SheetTrigger asChild>
        <PillButton icon={<Funnel className="size-3" />}>{t`Resume Settings`}</PillButton>
      </SheetTrigger>
      <SheetContent side="bottom" className="h-[85vh] max-h-[85vh] overflow-y-auto">
        <SheetHeader className="pb-4">
          <SheetTitle className="text-lg font-bold">{t`Resume Settings`}</SheetTitle>
          <SheetDescription className="text-base">
            {t`Customize your resume generation settings`}
          </SheetDescription>
        </SheetHeader>
        <div className="grid gap-6 py-4 pb-8">
          <div className="space-y-1">
            <label className="text-sm font-medium">{t`Language`}</label>
            <div className="grid grid-cols-2 gap-2">
              {LANGUAGES.map((lang) => (
                <button
                  key={lang.value}
                  type="button"
                  className={`min-h-[48px] rounded-lg border p-4 text-left transition-colors ${
                    watch("language") === lang.value
                      ? "border-primary bg-primary/10"
                      : "border-border hover:bg-muted"
                  }`}
                  onClick={() => {
                    setValue("language", lang.value);
                  }}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-sm">{getLanguageFlag(lang.value)}</span>
                    <span className="text-sm">{lang.label}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <Separator />

          <div className="space-y-3">
            <label className="text-sm font-medium">{t`Experience Level`}</label>
            <div className="grid grid-cols-2 gap-2">
              {EXPERIENCE_LEVELS.map((level) => (
                <button
                  key={level}
                  type="button"
                  className={`min-h-[48px] rounded-lg border p-4 text-left transition-colors ${
                    watch("experience") === level
                      ? "border-primary bg-primary/10"
                      : "border-border hover:bg-muted"
                  }`}
                  onClick={() => {
                    setValue("experience", level);
                  }}
                >
                  <span className="text-base">
                    {level} {t`years`}
                  </span>
                </button>
              ))}
            </div>
          </div>

          <Separator />

          <div className="space-y-3">
            <label className="text-sm font-medium">{t`Tone`}</label>
            <div className="grid grid-cols-2 gap-2">
              {TONE_OPTIONS.map((tone) => (
                <button
                  key={tone}
                  type="button"
                  className={`min-h-[48px] rounded-lg border p-4 text-left transition-colors ${
                    watch("tone") === tone
                      ? "border-primary bg-primary/10"
                      : "border-border hover:bg-muted"
                  }`}
                  onClick={() => {
                    setValue("tone", tone);
                  }}
                >
                  <span className="text-base">{getToneLabel(tone)}</span>
                </button>
              ))}
            </div>
          </div>

          <Separator />

          <div className="space-y-3">
            <label className="text-sm font-medium">{t`Country`}</label>
            <div className="grid max-h-32 grid-cols-1 gap-2 overflow-y-auto sm:max-h-48">
              {getCodes()
                .sort((a: string, b: string) => {
                  const nameA = getName(a) || a;
                  const nameB = getName(b) || b;
                  return nameA.localeCompare(nameB);
                })
                .map((code: string) => {
                  const name = getName(code) || code;
                  return (
                    <button
                      key={code}
                      type="button"
                      className={`min-h-[48px] rounded-lg border p-4 text-left transition-colors ${
                        watch("country") === code
                          ? "border-primary bg-primary/10"
                          : "border-border hover:bg-muted"
                      }`}
                      onClick={() => {
                        setValue("country", code);
                      }}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-sm">{getCountryFlag(code)}</span>
                        <span className="text-base">{name}</span>
                      </div>
                    </button>
                  );
                })}
            </div>
          </div>

          <Separator />

          <div className="space-y-3">
            <label className="text-sm font-medium">{t`ATS Score`}</label>
            <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-4 dark:border-emerald-800 dark:bg-emerald-900/20">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ChartBar className="size-5 text-emerald-600" />
                  <span className="text-base font-semibold text-emerald-700 dark:text-emerald-400">
                    85-95%
                  </span>
                  <div className="size-2 animate-pulse rounded-full bg-emerald-500"></div>
                </div>
                <span className="text-sm text-emerald-600 dark:text-emerald-400">{t`Optimized`}</span>
              </div>
              <p className="mt-2 text-xs text-emerald-600/80 dark:text-emerald-400/80">
                {t`Your resume will be optimized for Applicant Tracking Systems`}
              </p>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );

  const currentLang =
    HERO_LANGUAGES.find((l) => l.value === watch("language")) || HERO_LANGUAGES[0];
  const currentTone =
    HERO_TONE_OPTIONS.find((t) => t.value === watch("tone")) || HERO_TONE_OPTIONS[0];
  const atsTargetMin = watch("atsTargetMin") ?? 90;
  const currentAts = HERO_ATS_SCORES.find((a) => a.value === atsTargetMin) || HERO_ATS_SCORES[0];

  return (
    <div className="w-full" ref={blankContainerRef}>
      {/* Start path selector */}
      <div className="mx-auto mb-2 max-w-[820px] px-1">
        <div className="mb-3 flex items-center gap-4">
          <div className="h-px flex-1 bg-white/10" />
          <p className="text-center text-[11px] font-semibold tracking-[0.34em] text-white/70">
            {t`HOW DO YOU WANT TO START?`}
          </p>
          <div className="h-px flex-1 bg-white/10" />
        </div>

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {/* Use AI Builder — selected with gradient border + connecting arrow */}
          <div className="relative pt-3">
            {/* Recommended pill — overlaps top edge */}
            <div className="pointer-events-none absolute left-1/2 top-0 z-20 -translate-x-1/2">
              <span
                className="block rounded-md px-3 py-[5px] text-[10px] font-bold leading-none tracking-[0.01em] text-white"
                style={{
                  background: "linear-gradient(180deg, #35b6bf 0%, #1a7e8d 100%)",
                  boxShadow: "0 8px 20px rgba(39,190,203,0.35), 0 0 0 1px rgba(127,227,243,0.25)",
                }}
              >
                {t`Recommended`}
              </span>
            </div>

            <button
              type="button"
              onClick={() => setCreationMode("form")}
              className="relative block w-full rounded-[14px] p-[1.5px] text-center transition duration-200 hover:-translate-y-0.5"
              style={{
                background:
                  "linear-gradient(155deg, #6ddff3 0%, #4ec6d9 28%, #2a8a9a 55%, #d97c4d 85%, #ff8b5b 100%)",
                boxShadow:
                  "0 0 0 1px rgba(110,216,238,0.16), 0 0 30px rgba(92,226,244,0.26), 0 18px 42px rgba(255,139,91,0.18)",
              }}
            >
              <div
                className="rounded-[12.5px] px-3 pb-4 pt-6"
                style={{
                  background:
                    "radial-gradient(110% 95% at 52% 0%, rgba(42,104,125,0.92) 0%, rgba(18,45,70,0.96) 58%, rgba(14,35,56,0.98) 100%)",
                }}
              >
                <Sparkle size={22} className="mx-auto mb-2.5 text-[#9bebf6]" weight="fill" />
                <div className="text-[14px] font-semibold leading-tight text-white">{t`Use AI Builder`}</div>
                <div className="mt-1 text-[11px] leading-snug text-white/55">
                  {t`Generate from job details`}
                </div>
              </div>
            </button>

            {creationMode === "form" && (
              <div className="pointer-events-none absolute -bottom-7 -left-10 z-40 hidden sm:block">
                <svg width="54" height="58" viewBox="0 0 54 58" fill="none" aria-hidden>
                  <path
                    d="M7 4C4 23 14 38 38 39"
                    stroke="#ff9866"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeDasharray="4 5"
                  />
                  <path
                    d="M34 31L43 39L32 45"
                    stroke="#ff9866"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            )}
          </div>

          {/* Import Resume */}
          <div className="relative pt-2">
            <button
              type="button"
              onClick={() => (onUploadTabClick ? onUploadTabClick() : setCreationMode("upload"))}
              className={cn(
                "block w-full rounded-[14px] border px-3 pb-3.5 pt-5 text-center transition duration-200 hover:-translate-y-0.5",
                creationMode === "upload"
                  ? "border-[#7fe3f3]/60 bg-[#7fe3f3]/[0.06]"
                  : "border-white/[0.10] bg-[#0d2136]/80 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] hover:border-white/25 hover:bg-[#132b44]",
              )}
            >
              <CloudArrowUp size={20} className="mx-auto mb-2 text-[#7fe3f3]" weight="regular" />
              <div className="text-[14px] font-semibold leading-tight text-white">{t`Import Resume`}</div>
              <div className="mt-1 text-[11px] leading-snug text-white/55">
                {t`Improve an existing CV`}
              </div>
            </button>

            {/* Down-arrow indicator when this card is selected */}
            {creationMode === "upload" && (
              <div className="pointer-events-none absolute -bottom-3 left-1/2 z-40 -translate-x-1/2">
                <div
                  className="flex size-7 items-center justify-center rounded-full"
                  style={{
                    background: "linear-gradient(180deg, #ff9b6b 0%, #ff7a48 100%)",
                    boxShadow: "0 8px 18px rgba(255,122,72,0.55), 0 0 0 3px #0f1f33",
                  }}
                >
                  <CaretDown size={14} weight="bold" className="text-white" />
                </div>
              </div>
            )}
          </div>

          {/* Choose Template */}
          <div className="pt-2">
            <button
              type="button"
              onClick={() => navigate("/templates")}
              className="block w-full rounded-[14px] border border-white/[0.10] bg-[#0d2136]/80 px-3 pb-3.5 pt-5 text-center shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] transition duration-200 hover:-translate-y-0.5 hover:border-white/25 hover:bg-[#132b44]"
            >
              <Browsers size={20} className="mx-auto mb-2 text-[#7fe3f3]" weight="regular" />
              <div className="text-[14px] font-semibold leading-tight text-white">{t`Choose Template`}</div>
              <div className="mt-1 text-[11px] leading-snug text-white/55">{t`Start with a design`}</div>
            </button>
          </div>

          {/* Start from Example */}
          <div className="pt-2">
            <button
              type="button"
              onClick={() => navigate("/examples")}
              className="block w-full rounded-[14px] border border-white/[0.10] bg-[#0d2136]/80 px-3 pb-3.5 pt-5 text-center shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] transition duration-200 hover:-translate-y-0.5 hover:border-white/25 hover:bg-[#132b44]"
            >
              <File size={20} className="mx-auto mb-2 text-[#7fe3f3]" weight="regular" />
              <div className="text-[14px] font-semibold leading-tight text-white">{t`Start from Example`}</div>
              <div className="mt-1 text-[11px] leading-snug text-white/55">{t`Use a proven sample`}</div>
            </button>
          </div>
        </div>
      </div>

      {/* === FORM MODE === */}
      {creationMode === "form" && (
        <form onSubmit={handleSubmit(onSubmit)} className="w-full">
          <div className="relative mx-auto w-full max-w-[820px]">
            <div className="relative z-30 overflow-visible rounded-[18px] border border-[#58d8e9]/20 bg-[#071a2b]/80 p-4 shadow-[0_24px_80px_rgba(0,0,0,0.38),0_0_42px_rgba(48,201,220,0.10),inset_0_1px_0_rgba(255,255,255,0.06)] backdrop-blur-2xl sm:p-5">
              <div className="pointer-events-none absolute inset-x-8 top-0 h-px bg-gradient-to-r from-transparent via-[#6ee6ef]/55 to-transparent" />
              <div className="mb-1 flex items-center gap-2">
                <Sparkle size={16} weight="fill" className="text-[#7fe3f3]" />
                <span className="text-[13px] font-semibold tracking-[0.02em] text-[#7fe3f3]">
                  {t`AI Resume Builder`}
                </span>
              </div>
              <p className="mb-4 text-[12.5px] leading-snug text-white">
                {t`Enter a job title, paste a job link, or add a description — and our AI will build a tailored resume in seconds.`}
              </p>

              <div className="rounded-[13px] border-0 border-white/[0.08] bg-[#071a2b]/68 p-0 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
                <label className="mb-2 block text-[11px] font-medium text-white/55">
                  {t`Desired Job Title`} <span className="text-[#E8885E]">*</span>
                </label>

                <div className="flex min-h-[48px] items-center gap-3 rounded-[10px] border border-white/[0.11] bg-[#0b2034] px-4 text-white transition focus-within:border-[#7fe3f3]/40 focus-within:bg-[#10283f] focus-within:shadow-[0_0_24px_rgba(127,227,243,0.10)]">
                  <Briefcase size={18} className="shrink-0 text-white/45" />
                  <input
                    type="text"
                    value={watch("jobRole")}
                    onChange={(e) => setValue("jobRole", e.target.value)}
                    placeholder={jobRolePlaceholder}
                    className="min-w-0 flex-1 border-0 bg-transparent text-[15px] font-medium text-white outline-none placeholder:text-white/35 focus:ring-0"
                  />
                  <Tooltip content={t`Paste description`}>
                    <button
                      type="button"
                      onClick={() => {
                        setShowJobDescriptionInput((v) => !v);
                        if (showJobUrlInput) setShowJobUrlInput(false);
                      }}
                      className={cn(
                        "flex size-7 shrink-0 items-center justify-center rounded-full transition",
                        showJobDescriptionInput
                          ? "bg-white/15 text-white"
                          : "text-white/45 hover:bg-white/10 hover:text-white/80",
                      )}
                      aria-label={t`Paste description`}
                    >
                      <TextAlignLeft size={16} />
                    </button>
                  </Tooltip>
                  <Tooltip content={t`Add job URL`}>
                    <button
                      type="button"
                      onClick={() => {
                        setShowJobUrlInput((v) => !v);
                        if (showJobDescriptionInput) setShowJobDescriptionInput(false);
                      }}
                      className={cn(
                        "flex size-7 shrink-0 items-center justify-center rounded-full transition",
                        showJobUrlInput
                          ? "bg-white/15 text-white"
                          : "text-white/45 hover:bg-white/10 hover:text-white/80",
                      )}
                      aria-label={t`Add job URL`}
                    >
                      <LinkSimple size={16} />
                    </button>
                  </Tooltip>
                </div>

                <div className="mt-4 grid gap-3 sm:grid-cols-4">
                  <div className="relative" data-resume-form-popover>
                    <p className="mb-1.5 text-[11px] font-medium text-white/55">
                      {resumeLanguageLabel}
                    </p>
                    <button
                      type="button"
                      onClick={toggleHeroLanguageDropdown}
                      className="flex h-[42px] w-full items-center justify-between gap-2 rounded-[10px] border border-white/[0.10] bg-[#0b2034] px-3 text-left text-[13px] font-medium text-white transition hover:border-white/20 hover:bg-[#10283f]"
                    >
                      <span className="truncate">
                        {currentLang.flag} {currentLang.label}
                      </span>
                      <CaretDown size={13} className="shrink-0 text-white/45" />
                    </button>
                    {langMoreOpen && (
                      <div className="absolute left-0 top-full z-50 mt-2 w-52 overflow-hidden rounded-xl border border-white/10 bg-[#25313d] shadow-2xl">
                        <div className="max-h-[220px] overflow-y-auto py-1">
                          {HERO_LANGUAGES.map((lang) => (
                            <button
                              key={lang.value}
                              type="button"
                              onClick={() => {
                                setValue("language", lang.value);
                                setLangMoreOpen(false);
                              }}
                              className={cn(
                                "flex w-full items-center justify-between px-4 py-2.5 text-left text-[13px] text-white/75 transition hover:bg-white/10",
                                watch("language") === lang.value &&
                                  "bg-white/10 font-semibold text-white",
                              )}
                            >
                              <span>
                                {lang.flag} {lang.label}
                              </span>
                              {watch("language") === lang.value && (
                                <Check size={13} className="shrink-0 text-[#6dd4e8]" />
                              )}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="relative" data-resume-form-popover>
                    <p className="mb-1.5 text-[11px] font-medium text-white/55">
                      {experienceLabel}
                    </p>
                    <button
                      type="button"
                      onClick={() => toggleHeroDropdown("years")}
                      className="flex h-[42px] w-full items-center justify-between gap-2 rounded-[10px] border border-white/[0.10] bg-[#0b2034] px-3 text-left text-[13px] font-medium text-white transition hover:border-white/20 hover:bg-[#10283f]"
                    >
                      <span>
                        {watch("experience")
                          ? `${watch("experience")} ${t`years`}`
                          : `2-4 ${t`years`}`}
                      </span>
                      <CaretDown size={13} className="shrink-0 text-white/45" />
                    </button>
                    {openBlank === "years" && (
                      <div className="absolute left-0 top-full z-50 mt-2 w-52 overflow-hidden rounded-xl border border-white/10 bg-[#25313d] p-3 shadow-2xl">
                        <div className="grid grid-cols-3 gap-1.5">
                          {EXPERIENCE_LEVELS.map((lvl) => (
                            <button
                              key={lvl}
                              type="button"
                              onClick={() => {
                                setValue("experience", lvl);
                                setOpenBlank(null);
                              }}
                              className={cn(
                                "rounded-lg py-2.5 text-[13px] font-semibold transition",
                                watch("experience") === lvl
                                  ? "bg-white text-[#16344c]"
                                  : "border border-white/10 text-white/70 hover:border-white/25 hover:text-white",
                              )}
                            >
                              {lvl}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="relative" data-resume-form-popover>
                    <p className="mb-1.5 text-[11px] font-medium text-white/55">{t`Country`}</p>
                    <button
                      type="button"
                      onClick={() => toggleHeroDropdown("location")}
                      className="flex h-[42px] w-full items-center justify-between gap-2 rounded-[10px] border border-white/[0.10] bg-[#0b2034] px-3 text-left text-[13px] font-medium text-white transition hover:border-white/20 hover:bg-[#10283f]"
                    >
                      <span className="truncate">
                        {getCountryFlag(watch("country"))}{" "}
                        {getName(watch("country")) || watch("country")}
                      </span>
                      <CaretDown size={13} className="shrink-0 text-white/45" />
                    </button>
                    {openBlank === "location" && (
                      <div className="absolute left-0 top-full z-50 mt-2 w-72 overflow-hidden rounded-xl border border-white/10 bg-[#25313d] p-2 shadow-2xl">
                        <Command className="bg-transparent text-white [&_[cmdk-input-wrapper]]:border-0">
                          <div className="mx-1 mb-1 flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.06] px-3 py-2.5 focus-within:border-[#6dd4e8]/50">
                            <MagnifyingGlass size={13} className="shrink-0 text-white/40" />
                            <CommandInput
                              placeholder={t`Search country...`}
                              className="h-auto border-0 bg-transparent p-0 text-[13px] text-white shadow-none outline-none ring-0 placeholder:text-white/35 focus:ring-0"
                            />
                          </div>
                          <CommandList className="max-h-[200px]">
                            <CommandEmpty className="py-3 text-center text-[13px] text-white/40">{t`No country found`}</CommandEmpty>
                            <CommandGroup>
                              {heroCountries.map((c: { code: string; name: string }) => (
                                <CommandItem
                                  key={c.code}
                                  value={c.name}
                                  onSelect={() => {
                                    setValue("country", c.code);
                                    setOpenBlank(null);
                                  }}
                                  className={cn(
                                    "text-[13px] text-white/75",
                                    watch("country") === c.code && "font-semibold text-white",
                                  )}
                                >
                                  {getCountryFlag(c.code)} {c.name}
                                  {watch("country") === c.code && (
                                    <Check size={13} className="ml-auto text-[#6dd4e8]" />
                                  )}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </div>
                    )}
                  </div>

                  <div className="relative" data-resume-form-popover>
                    <p className="mb-1.5 text-[11px] font-medium text-white/55">
                      {writingToneLabel}
                    </p>
                    <button
                      type="button"
                      onClick={() => toggleHeroDropdown("tone")}
                      className="flex h-[42px] w-full items-center justify-between gap-2 rounded-[10px] border border-white/[0.10] bg-[#0b2034] px-3 text-left text-[13px] font-medium text-white transition hover:border-white/20 hover:bg-[#10283f]"
                    >
                      <span className="truncate">{currentTone.label}</span>
                      <CaretDown size={13} className="shrink-0 text-white/45" />
                    </button>
                    {openBlank === "tone" && (
                      <div className="absolute left-0 top-full z-50 mt-2 w-60 overflow-hidden rounded-xl border border-white/10 bg-[#25313d] p-1.5 shadow-2xl">
                        {[
                          { value: "Professional", label: t`Professional`, emoji: "💼" },
                          { value: "Formal", label: t`Formal`, emoji: "🏛️" },
                          { value: "Friendly", label: t`Friendly`, emoji: "😊" },
                          { value: "Creative", label: t`Creative`, emoji: "🎨" },
                          { value: "Technical", label: t`Technical`, emoji: "⚙️" },
                        ].map((opt) => (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => {
                              setValue("tone", opt.value);
                              setOpenBlank(null);
                            }}
                            className={cn(
                              "flex w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-left text-[13px] transition",
                              watch("tone") === opt.value
                                ? "bg-white/10 font-semibold text-white"
                                : "text-white/70 hover:bg-white/10 hover:text-white",
                            )}
                          >
                            <span>{opt.emoji}</span>
                            {opt.label}
                            {watch("tone") === opt.value && (
                              <Check size={13} className="ml-auto text-[#6dd4e8]" />
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {showJobUrlInput && (
                <div className="mt-4 rounded-2xl border border-white/10 bg-white/[0.06]">
                  <div className="flex items-center gap-2 px-4 py-3">
                    <LinkSimple size={14} className="shrink-0 text-white/45" />
                    <input
                      type="url"
                      value={watch("jobUrl") || ""}
                      onChange={(e) => {
                        setValue("jobUrl", e.target.value);
                        setJobPreview(null);
                      }}
                      placeholder="https://jobs.example.com/software-engineer"
                      className="flex-1 border-0 bg-transparent text-[13px] text-white outline-none placeholder:text-white/35 focus:ring-0"
                    />
                    {watch("jobUrl") && !jobPreview && (
                      <button
                        type="button"
                        onClick={handlePreviewUrl}
                        disabled={isPreviewLoading}
                        className="shrink-0 whitespace-nowrap text-[11px] font-semibold text-[#6dd4e8] hover:underline disabled:opacity-50"
                      >
                        {isPreviewLoading ? (
                          <CircleNotch size={11} className="inline animate-spin" />
                        ) : (
                          t`Preview`
                        )}
                      </button>
                    )}
                    {watch("jobUrl") && (
                      <button
                        type="button"
                        onClick={() => {
                          setValue("jobUrl", "");
                          setJobPreview(null);
                        }}
                        className="shrink-0 text-white/45 hover:text-white"
                      >
                        <X size={13} />
                      </button>
                    )}
                  </div>
                </div>
              )}

              {jobPreview && showJobUrlInput && (
                <div className="mt-3 rounded-2xl border border-white/15 bg-white/[0.08] p-4">
                  <div className="mb-2 flex items-start justify-between gap-3">
                    <p className="text-[12px] font-bold leading-snug text-white">
                      {jobPreview.title}
                    </p>
                    <button
                      type="button"
                      onClick={() => setJobPreview(null)}
                      className="mt-0.5 shrink-0 text-white/45 hover:text-white"
                    >
                      <X size={13} />
                    </button>
                  </div>

                  <div className="mb-2 flex flex-wrap gap-1.5 text-[10px] text-white/75">
                    {[
                      jobPreview.company,
                      jobPreview.location,
                      jobPreview.employmentType,
                      jobPreview.experience,
                      jobPreview.salary !== "Not specified" ? jobPreview.salary : null,
                    ]
                      .filter(Boolean)
                      .map((chip) => (
                        <span
                          key={chip}
                          className="rounded-full border border-white/15 bg-white/[0.08] px-2 py-0.5"
                        >
                          {chip}
                        </span>
                      ))}
                  </div>

                  {jobPreview.summary && (
                    <p className="text-[10px] leading-relaxed text-white/70">
                      {jobPreview.summary}
                    </p>
                  )}
                  {jobPreview.skills && (
                    <p className="mt-2 text-[10px] text-white/70">
                      <span className="font-semibold text-white/90">{t`Skills:`}</span>{" "}
                      {jobPreview.skills}
                    </p>
                  )}
                  {jobPreview.industry && (
                    <p className="mt-2 text-[10px] text-white/70">
                      <span className="font-semibold text-white/90">{t`Industry:`}</span>{" "}
                      {jobPreview.industry}
                    </p>
                  )}
                  {jobPreview.jobRequirements && (
                    <div className="mt-2">
                      <p className="text-[10px] font-semibold text-white/90">{t`Key requirements:`}</p>
                      <p className="mt-1 whitespace-pre-line text-[10px] leading-relaxed text-white/70">
                        {jobPreview.jobRequirements}
                      </p>
                    </div>
                  )}
                  {jobPreview.requirements && (
                    <div className="mt-2">
                      <p className="text-[10px] font-semibold text-white/90">{t`Requirements:`}</p>
                      <p className="mt-1 whitespace-pre-line text-[10px] leading-relaxed text-white/70">
                        {jobPreview.requirements}
                      </p>
                    </div>
                  )}
                  {jobPreview.responsibilities && (
                    <div className="mt-2">
                      <p className="text-[10px] font-semibold text-white/90">{t`Responsibilities:`}</p>
                      <p className="mt-1 whitespace-pre-line text-[10px] leading-relaxed text-white/70">
                        {jobPreview.responsibilities}
                      </p>
                    </div>
                  )}
                  {jobPreview.benefits && (
                    <div className="mt-2">
                      <p className="text-[10px] font-semibold text-white/90">{t`Benefits:`}</p>
                      <p className="mt-1 whitespace-pre-line text-[10px] leading-relaxed text-white/70">
                        {jobPreview.benefits}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {showJobDescriptionInput && (
                <div className="mt-4 rounded-2xl border border-white/10 bg-white/[0.06]">
                  <textarea
                    value={watch("additionalDescription") || ""}
                    onChange={(e) => setValue("additionalDescription", e.target.value)}
                    placeholder={t`Paste the job description here — our agents will tailor your resume to match it.`}
                    rows={4}
                    className="w-full resize-none rounded-2xl border-0 bg-transparent px-4 py-3 text-[13px] leading-relaxed text-white outline-none placeholder:text-white/35 focus:ring-0"
                  />
                </div>
              )}
            </div>

            <button
              type="button"
              disabled={isLoading}
              onClick={handleButtonClick}
              className="hero-cta-shimmer relative z-10 mt-5 flex min-h-[58px] w-full items-center justify-center gap-3 overflow-hidden rounded-full px-7 text-[18px] font-extrabold tracking-[0.01em] text-white shadow-[0_18px_44px_rgba(232,136,94,0.30),0_0_42px_rgba(46,213,222,0.16)] transition duration-200 hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-60"
              style={{ background: "linear-gradient(100deg,#268599 0%,#51a8a7 42%,#E8885E 100%)" }}
            >
              <span className="hero-cta-shimmer-bg" />
              {isLoading ? (
                <CircleNotch size={20} className="relative animate-spin" />
              ) : (
                <Sparkle size={21} weight="fill" className="relative" />
              )}
              <span className="relative">{buildCtaLabel}</span>
              <ArrowRight size={20} weight="bold" className="relative rtl:rotate-180" />
            </button>

            {errors.jobRole && (
              <p className="mt-3 text-center text-sm font-medium text-red-300">
                {errors.jobRole.message}
              </p>
            )}

            <div className="mt-5 flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-[13px] text-gray-300">
              {[
                { text: t`+95% ATS pass rate`, strong: true },
                { text: t`No credit card is required` },
                { text: t`Ready in two minutes` },
              ].map((item) => (
                <div key={item.text} className="flex items-center gap-2">
                  <span className="flex size-4 shrink-0 items-center justify-center rounded-full bg-[#14d4a0] text-[#10242c]">
                    <Check size={11} weight="bold" />
                  </span>
                  <span className={cn(item.strong && "font-extrabold text-gray-200")}>
                    {item.text}
                  </span>
                </div>
              ))}

              <div className="flex items-center gap-2">
                <Star size={16} weight="fill" className="shrink-0 text-[#14d4a0]" />
                <span>
                  <strong className="font-extrabold text-gray-200">Trustpilot</strong>{" "}
                  {t`4.8 | 37,389 reviews`}
                </span>
              </div>

              <div className="flex items-center gap-3">
                <div className="flex" aria-hidden>
                  {[
                    "/assets/images/professionals/1.png",
                    "/assets/images/professionals/2.png",
                    "/assets/images/professionals/3.png",
                    "/assets/images/professionals/4.png",
                    "/assets/images/professionals/5.png",
                  ].map((src, index) => (
                    <img
                      key={src}
                      src={src}
                      alt=""
                      className="size-7 rounded-full border-2 border-[#25313d] object-cover"
                      style={{ marginLeft: index === 0 ? 0 : -8 }}
                    />
                  ))}
                  <span className="-ml-2 flex size-7 items-center justify-center rounded-full border-2 border-[#25313d] bg-[#155469] text-[10px] font-bold text-[#6dd4e8]">
                    +6
                  </span>
                </div>
                <span>
                  <strong className="font-extrabold text-gray-200">{t`+6 Million Users`}</strong>{" "}
                  {t`trust Stylion across 150+ countries`}
                </span>
              </div>
            </div>
          </div>

          {/* Prompt card */}
          <div
            className="hidden"
            style={{
              boxShadow: "0 1px 0 rgba(22,52,76,0.02), 0 24px 48px -24px rgba(22,52,76,0.10)",
            }}
          >
            {/* ✦ brand icon */}
            <div
              className="absolute -top-4 left-8 flex size-9 items-center justify-center rounded-full text-[17px] font-bold text-white"
              style={{
                background: "linear-gradient(135deg,#155469,#268599,#2eb89a,#E8885E)",
                boxShadow: "0 8px 20px rgba(232,136,94,0.30)",
              }}
            >
              ✦
            </div>

            <p className="mb-5 text-[11px] font-bold uppercase tracking-[0.18em] text-[#268599]">
              {t`Your brief — tap any blank to edit`}
            </p>

            {/* Mad-lib story text */}
            <div
              className="relative font-serif text-[26px] font-medium leading-[1.7]"
              style={{ letterSpacing: "-0.01em", color: "#171717" }}
            >
              {t`I'm a`}&nbsp;
              {/* Role blank */}
              <span className="relative inline-block">
                <button
                  type="button"
                  onClick={() => setOpenBlank(openBlank === "role" ? null : "role")}
                  className="relative inline-flex cursor-pointer items-center gap-1 px-1 font-serif font-bold transition-colors"
                  style={{
                    color: watch("jobRole") ? "#155469" : "#9ca3af",
                    fontStyle: watch("jobRole") ? "normal" : "italic",
                    fontWeight: watch("jobRole") ? 700 : 500,
                  }}
                >
                  {watch("jobRole") || t`Job Title`}
                  <CaretDown
                    size={14}
                    className="mt-0.5 shrink-0 opacity-60"
                    style={{ color: watch("jobRole") ? "#155469" : "#9ca3af" }}
                  />
                  <span className="pointer-events-none absolute -bottom-1 left-0.5 right-0.5 block h-2 overflow-hidden">
                    <svg viewBox="0 0 200 8" preserveAspectRatio="none" className="h-full w-full">
                      <path
                        d="M2 5 Q40 1 80 5 T160 4 T198 5"
                        fill="none"
                        stroke="#E8885E"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  </span>
                </button>
                {openBlank === "role" && (
                  <div className="absolute left-0 top-full z-50 mt-2 w-72 overflow-hidden rounded-xl border border-gray-100 bg-white font-sans shadow-xl">
                    {/* Header label */}
                    <div className="px-4 pb-1 pt-3">
                      <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-gray-400">{t`Job Title`}</p>
                    </div>
                    {/* Search input */}
                    <div className="mx-3 mb-1 flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-1 transition-all focus-within:border-[#268599] focus-within:ring-2 focus-within:ring-[#268599]/10">
                      <MagnifyingGlass size={13} className="shrink-0 text-gray-400" />
                      <input
                        autoFocus
                        type="text"
                        value={watch("jobRole")}
                        onChange={(e) => setValue("jobRole", e.target.value)}
                        placeholder={t`Search...`}
                        className="flex-1 border-0 bg-transparent font-sans text-[13px] text-gray-800 outline-none placeholder:text-gray-400"
                        onKeyDown={(e) => e.key === "Enter" && setOpenBlank(null)}
                      />
                    </div>
                    {/* Suggestions list */}
                    <div
                      className="max-h-[220px] overflow-y-auto py-1 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar]:w-[5px]"
                      style={{ scrollbarWidth: "thin", scrollbarColor: "#d1d5db transparent" }}
                    >
                      {[
                        "Senior Product Designer",
                        "Product Designer",
                        "UX Designer",
                        "UI Designer",
                        "Design Lead",
                        "Design Manager",
                        "Product Manager",
                        "Software Engineer",
                        "Marketing Manager",
                        "Data Analyst",
                        "Project Manager",
                        "UX Researcher",
                      ]
                        .filter(
                          (r) =>
                            !watch("jobRole") ||
                            r.toLowerCase().includes(watch("jobRole").toLowerCase()),
                        )
                        .map((r) => (
                          <button
                            key={r}
                            type="button"
                            onClick={() => {
                              setValue("jobRole", r);
                              setOpenBlank(null);
                            }}
                            className={cn(
                              "flex w-full items-center justify-between px-4 py-3.5 text-left font-sans text-[15px] text-gray-800 transition hover:bg-gray-50",
                              watch("jobRole") === r && "bg-gray-100",
                            )}
                          >
                            {r}
                            {watch("jobRole") === r && (
                              <Check size={14} className="shrink-0 text-[#268599]" />
                            )}
                          </button>
                        ))}
                    </div>
                    {/* OR ADD YOUR OWN */}
                    <div className="border-t border-gray-100 px-4 py-3">
                      <p className="mb-2 font-sans text-[10px] font-bold uppercase tracking-[0.16em] text-gray-400">{t`Or add your own`}</p>
                      <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-1 transition-all focus-within:border-[#268599] focus-within:ring-2 focus-within:ring-[#268599]/10">
                        <input
                          type="text"
                          value={roleCustomInput}
                          onChange={(e) => setRoleCustomInput(e.target.value)}
                          placeholder={t`Type your own...`}
                          className="flex-1 border-0 bg-transparent font-sans text-[13px] text-gray-800 outline-none placeholder:text-gray-400"
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && roleCustomInput.trim()) {
                              setValue("jobRole", roleCustomInput.trim());
                              setRoleCustomInput("");
                              setOpenBlank(null);
                            }
                          }}
                        />
                        <button
                          type="button"
                          disabled={!roleCustomInput.trim()}
                          onClick={() => {
                            if (roleCustomInput.trim()) {
                              setValue("jobRole", roleCustomInput.trim());
                              setRoleCustomInput("");
                              setOpenBlank(null);
                            }
                          }}
                          className="flex size-6 shrink-0 items-center justify-center rounded-md bg-[#268599] text-white transition disabled:opacity-30"
                        >
                          <Check size={13} weight="bold" />
                        </button>
                      </div>
                    </div>
                    {/* Hint */}
                    <div className="border-t border-gray-100 px-4 py-2">
                      <p className="font-sans text-[10px] text-gray-400">
                        <kbd className="rounded border border-gray-200 bg-gray-100 px-1 py-0.5 font-mono text-[9px]">
                          Esc
                        </kbd>{" "}
                        {t`to close`} · {t`tap to edit`}
                      </p>
                    </div>
                  </div>
                )}
              </span>
              &nbsp;{t`with`}&nbsp;
              {/* Years blank */}
              <span className="relative inline-block">
                <button
                  type="button"
                  onClick={() => setOpenBlank(openBlank === "years" ? null : "years")}
                  className="relative inline-flex cursor-pointer items-center gap-1 px-1 font-serif font-bold transition-colors"
                  style={{
                    color: watch("experience") ? "#155469" : "#9ca3af",
                    fontStyle: watch("experience") ? "normal" : "italic",
                    fontWeight: watch("experience") ? 700 : 500,
                  }}
                >
                  {watch("experience") ? `${watch("experience")} ${t`years`}` : t`Years of exp.`}
                  <CaretDown
                    size={14}
                    className="mt-0.5 shrink-0 opacity-60"
                    style={{ color: watch("experience") ? "#155469" : "#9ca3af" }}
                  />
                  <span className="pointer-events-none absolute -bottom-1 left-0.5 right-0.5 block h-2 overflow-hidden">
                    <svg viewBox="0 0 200 8" preserveAspectRatio="none" className="h-full w-full">
                      <path
                        d="M2 5 Q40 1 80 5 T160 4 T198 5"
                        fill="none"
                        stroke="#E8885E"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  </span>
                </button>
                {openBlank === "years" && (
                  <div className="absolute left-0 top-full z-50 mt-2 w-52 overflow-hidden rounded-xl border border-gray-100 bg-white p-3 font-sans shadow-xl">
                    <div className="grid grid-cols-3 gap-1.5">
                      {EXPERIENCE_LEVELS.map((lvl) => (
                        <button
                          key={lvl}
                          type="button"
                          onClick={() => {
                            setValue("experience", lvl);
                            setOpenBlank(null);
                          }}
                          className={cn(
                            "rounded-lg py-2.5 font-sans text-[13px] font-semibold transition",
                            watch("experience") === lvl
                              ? "bg-[#155469] text-white"
                              : "border border-gray-200 text-gray-600 hover:border-[#268599] hover:text-[#268599]",
                          )}
                        >
                          {lvl}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </span>
              &nbsp;{t`of experience, targeting roles in`}&nbsp;
              {/* Location blank — country picker */}
              <span className="relative inline-block">
                <button
                  type="button"
                  onClick={() => setOpenBlank(openBlank === "location" ? null : "location")}
                  className="relative inline-flex cursor-pointer items-center gap-1 px-1 font-serif font-bold transition-colors"
                  style={{
                    color: watch("country") ? "#155469" : "#9ca3af",
                    fontStyle: watch("country") ? "normal" : "italic",
                    fontWeight: watch("country") ? 700 : 500,
                  }}
                >
                  {watch("country") ? getName(watch("country")) || watch("country") : t`country`}
                  <CaretDown
                    size={14}
                    className="mt-0.5 shrink-0 opacity-60"
                    style={{ color: watch("country") ? "#155469" : "#9ca3af" }}
                  />
                  <span className="pointer-events-none absolute -bottom-1 left-0.5 right-0.5 block h-2 overflow-hidden">
                    <svg viewBox="0 0 200 8" preserveAspectRatio="none" className="h-full w-full">
                      <path
                        d="M2 5 Q40 1 80 5 T160 4 T198 5"
                        fill="none"
                        stroke="#E8885E"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  </span>
                </button>
                {openBlank === "location" && (
                  <div className="absolute left-0 top-full z-50 mt-2 w-72 overflow-hidden rounded-xl border border-gray-100 bg-white font-sans shadow-xl">
                    <div className="p-2">
                      <Command className="[&_[cmdk-input-wrapper]]:border-0">
                        <div className="mx-1 mb-1 flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2.5 transition-all focus-within:border-[#268599] focus-within:ring-2 focus-within:ring-[#268599]/10">
                          <MagnifyingGlass size={13} className="shrink-0 text-gray-400" />
                          <CommandInput
                            placeholder={t`Search country...`}
                            className="h-auto border-0 bg-transparent p-0 font-sans text-[13px] text-gray-800 shadow-none outline-none ring-0 focus:ring-0"
                          />
                        </div>
                        <CommandList className="max-h-[200px]">
                          <CommandEmpty className="py-3 text-center font-sans text-[13px] text-gray-400">{t`No country found`}</CommandEmpty>
                          <CommandGroup>
                            {heroCountries.map((c: { code: string; name: string }) => (
                              <CommandItem
                                key={c.code}
                                value={c.name}
                                onSelect={() => {
                                  setValue("country", c.code);
                                  setOpenBlank(null);
                                }}
                                className={cn(
                                  "font-sans text-[13px]",
                                  watch("country") === c.code && "font-semibold text-[#268599]",
                                )}
                              >
                                {getCountryFlag(c.code)} {c.name}
                                {watch("country") === c.code && (
                                  <Check size={13} className="ml-auto text-[#268599]" />
                                )}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </div>
                  </div>
                )}
              </span>
              {". "}
              {t`I want my CV to read`}&nbsp;
              {/* Tone blank */}
              <span className="relative inline-block">
                <button
                  type="button"
                  onClick={() => setOpenBlank(openBlank === "tone" ? null : "tone")}
                  className="relative inline-flex cursor-pointer items-center gap-1 px-1 font-serif font-bold transition-colors"
                  style={{
                    color: watch("tone") ? "#155469" : "#9ca3af",
                    fontStyle: watch("tone") ? "normal" : "italic",
                    fontWeight: watch("tone") ? 700 : 500,
                  }}
                >
                  {watch("tone") || t`confident`}
                  <CaretDown
                    size={14}
                    className="mt-0.5 shrink-0 opacity-60"
                    style={{ color: watch("tone") ? "#155469" : "#9ca3af" }}
                  />
                  <span className="pointer-events-none absolute -bottom-1 left-0.5 right-0.5 block h-2 overflow-hidden">
                    <svg viewBox="0 0 200 8" preserveAspectRatio="none" className="h-full w-full">
                      <path
                        d="M2 5 Q40 1 80 5 T160 4 T198 5"
                        fill="none"
                        stroke="#E8885E"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  </span>
                </button>
                {openBlank === "tone" && (
                  <div className="absolute left-0 top-full z-50 mt-2 w-60 overflow-hidden rounded-xl border border-gray-100 bg-white p-1.5 font-sans shadow-xl">
                    {[
                      { value: "Professional", label: t`Professional`, emoji: "💼" },
                      { value: "Formal", label: t`Formal`, emoji: "🏛️" },
                      { value: "Friendly", label: t`Friendly`, emoji: "😊" },
                      { value: "Creative", label: t`Creative`, emoji: "🎨" },
                      { value: "Technical", label: t`Technical`, emoji: "⚙️" },
                      { value: "Executive", label: t`Executive`, emoji: "👔" },
                    ].map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => {
                          setValue("tone", opt.value);
                          setOpenBlank(null);
                        }}
                        className={cn(
                          "flex w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-left font-sans text-[13px] transition",
                          watch("tone") === opt.value
                            ? "bg-[rgba(38,133,153,0.08)] font-semibold text-[#155469]"
                            : "text-gray-700 hover:bg-gray-50",
                        )}
                      >
                        <span>{opt.emoji}</span>
                        {opt.label}
                        {watch("tone") === opt.value && (
                          <Check size={13} className="ml-auto text-[#268599]" />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </span>
              &nbsp;{t`and`}&nbsp;
              {/* Vibe blank */}
              <span className="relative inline-block">
                <button
                  type="button"
                  onClick={() => setOpenBlank(openBlank === "vibe" ? null : "vibe")}
                  className="relative inline-flex cursor-pointer items-center gap-1 px-1 font-serif font-bold transition-colors"
                  style={{
                    color: vibe ? "#155469" : "#9ca3af",
                    fontStyle: vibe ? "normal" : "italic",
                    fontWeight: vibe ? 700 : 500,
                  }}
                >
                  {vibe || t`add a vibe`}
                  <CaretDown
                    size={14}
                    className="mt-0.5 shrink-0 opacity-60"
                    style={{ color: vibe ? "#155469" : "#9ca3af" }}
                  />
                  <span className="pointer-events-none absolute -bottom-1 left-0.5 right-0.5 block h-2 overflow-hidden">
                    <svg viewBox="0 0 200 8" preserveAspectRatio="none" className="h-full w-full">
                      <path
                        d="M2 5 Q40 1 80 5 T160 4 T198 5"
                        fill="none"
                        stroke="#E8885E"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  </span>
                </button>
                {openBlank === "vibe" && (
                  <div className="absolute left-0 top-full z-50 mt-2 w-64 overflow-hidden rounded-xl border border-gray-100 bg-white p-1.5 font-sans shadow-xl">
                    {[
                      "Impossible to ignore 🔥",
                      "Calm and senior 🌿",
                      "Sharp and modern ⚡",
                      "Warm and curious 🌸",
                      "Quietly excellent",
                      "Story-led 📖",
                    ].map((v) => (
                      <button
                        key={v}
                        type="button"
                        onClick={() => {
                          setVibe(v);
                          setOpenBlank(null);
                        }}
                        className={cn(
                          "flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-left text-[13px] transition",
                          vibe === v
                            ? "bg-[rgba(38,133,153,0.08)] font-semibold text-[#155469]"
                            : "text-gray-700 hover:bg-gray-50",
                        )}
                      >
                        {v}
                        {vibe === v && <Check size={13} className="ml-auto text-[#268599]" />}
                      </button>
                    ))}
                  </div>
                )}
              </span>
              {"."}
            </div>

            {/* Strip: Language + Length */}
            <div className="mt-8 grid grid-cols-2 gap-7 border-t border-dashed border-gray-200 pt-7">
              {/* Language */}
              <div>
                <p className="mb-2.5 text-[11px] font-bold uppercase tracking-[0.18em] text-gray-400">{t`Language`}</p>
                <div className="relative inline-flex items-center gap-1 rounded-xl bg-gray-100 p-1">
                  {[
                    { value: "EN", label: "🇺🇸 English" },
                    { value: "AR", label: "🇸🇦 العربية" },
                    { value: "FR", label: "🇫🇷 Français" },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => {
                        setValue("language", opt.value);
                        setLangMoreOpen(false);
                      }}
                      className={cn(
                        "rounded-lg px-3.5 py-2 text-[12px] font-semibold transition-all",
                        watch("language") === opt.value
                          ? "bg-white text-[#155469] shadow-sm"
                          : "text-gray-500 hover:text-gray-700",
                      )}
                    >
                      {opt.label}
                    </button>
                  ))}
                  {/* More languages button */}
                  <button
                    type="button"
                    onClick={() => setLangMoreOpen((v) => !v)}
                    className={cn(
                      "rounded-lg px-2.5 py-2 text-[13px] font-bold leading-none transition-all",
                      langMoreOpen
                        ? "bg-white text-[#155469] shadow-sm"
                        : "text-gray-400 hover:text-gray-600",
                    )}
                    title={t`More languages`}
                  >
                    ···
                  </button>
                  {/* More languages dropdown */}
                  {langMoreOpen && (
                    <div className="absolute left-0 top-full z-50 mt-2 w-52 overflow-hidden rounded-xl border border-gray-100 bg-white shadow-xl">
                      <div className="max-h-[220px] overflow-y-auto py-1">
                        {HERO_LANGUAGES.filter((l) => !["EN", "AR", "FR"].includes(l.value)).map(
                          (lang) => (
                            <button
                              key={lang.value}
                              type="button"
                              onClick={() => {
                                setValue("language", lang.value);
                                setLangMoreOpen(false);
                              }}
                              className={cn(
                                "flex w-full items-center justify-between px-4 py-2.5 text-left text-[13px] text-gray-800 transition hover:bg-gray-50",
                                watch("language") === lang.value &&
                                  "bg-[rgba(38,133,153,0.06)] font-semibold text-[#155469]",
                              )}
                            >
                              <span>
                                {lang.flag} {lang.label}
                              </span>
                              {watch("language") === lang.value && (
                                <Check size={13} className="shrink-0 text-[#268599]" />
                              )}
                            </button>
                          ),
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {/* Length */}
              <div>
                <p className="mb-2.5 text-[11px] font-bold uppercase tracking-[0.18em] text-gray-400">{t`Length`}</p>
                <div className="inline-flex gap-1 rounded-xl bg-gray-100 p-1">
                  {[
                    { value: "1", label: t`1 page` },
                    { value: "2", label: t`2 pages` },
                    { value: "auto", label: t`Auto` },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setPageCount(opt.value as "1" | "2" | "auto")}
                      className={cn(
                        "rounded-lg px-3.5 py-2 text-[12px] font-semibold transition-all",
                        pageCount === opt.value
                          ? "bg-white text-[#155469] shadow-sm"
                          : "text-gray-500 hover:text-gray-700",
                      )}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Job context row — URL or description chips */}
            <div className="mt-6">
              {/* Chip toggles */}
              <div className="flex items-center gap-2">
                <span className="text-[11px] text-gray-400">{t`Target a specific role?`}</span>
                <button
                  type="button"
                  onClick={() => {
                    setShowJobUrlInput((v) => !v);
                    if (showJobDescriptionInput) setShowJobDescriptionInput(false);
                  }}
                  className={cn(
                    "flex items-center gap-1 rounded-full border px-3 py-1 text-[11px] font-semibold transition-all",
                    showJobUrlInput
                      ? "border-[#268599] bg-[rgba(38,133,153,0.08)] text-[#155469]"
                      : "border-gray-200 bg-white text-gray-500 hover:border-[#268599] hover:text-[#268599]",
                  )}
                >
                  <LinkSimple size={11} weight="bold" />
                  {t`Add job URL`}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowJobDescriptionInput((v) => !v);
                    if (showJobUrlInput) setShowJobUrlInput(false);
                  }}
                  className={cn(
                    "flex items-center gap-1 rounded-full border px-3 py-1 text-[11px] font-semibold transition-all",
                    showJobDescriptionInput
                      ? "border-[#268599] bg-[rgba(38,133,153,0.08)] text-[#155469]"
                      : "border-gray-200 bg-white text-gray-500 hover:border-[#268599] hover:text-[#268599]",
                  )}
                >
                  <TextAlignLeft size={11} weight="bold" />
                  {t`Paste description`}
                </button>
              </div>

              {/* URL input panel */}
              {showJobUrlInput && (
                <div className="mt-3 rounded-xl border border-gray-200 bg-gray-50 transition-all focus-within:border-[#268599] focus-within:ring-2 focus-within:ring-[#268599]/10">
                  {/* Input row */}
                  <div className="flex items-center gap-2 px-3 py-2">
                    <LinkSimple size={14} className="shrink-0 text-gray-400" />
                    <input
                      type="url"
                      value={watch("jobUrl") || ""}
                      onChange={(e) => {
                        setValue("jobUrl", e.target.value);
                        setJobPreview(null);
                      }}
                      placeholder="https://jobs.example.com/software-engineer"
                      className="flex-1 border-0 bg-transparent text-[13px] text-gray-800 outline-none placeholder:text-gray-400"
                    />
                    {watch("jobUrl") && !jobPreview && (
                      <button
                        type="button"
                        onClick={handlePreviewUrl}
                        disabled={isPreviewLoading}
                        className="shrink-0 whitespace-nowrap text-[11px] font-semibold text-[#268599] hover:underline disabled:opacity-50"
                      >
                        {isPreviewLoading ? (
                          <CircleNotch size={11} className="inline animate-spin" />
                        ) : (
                          t`Preview`
                        )}
                      </button>
                    )}
                    {watch("jobUrl") && (
                      <button
                        type="button"
                        onClick={() => {
                          setValue("jobUrl", "");
                          setJobPreview(null);
                        }}
                        className="shrink-0 text-gray-400 hover:text-gray-600"
                      >
                        <X size={13} />
                      </button>
                    )}
                  </div>

                  {/* Preview card */}
                  {jobPreview ? (
                    <div className="max-h-64 space-y-2 overflow-y-auto border-t border-gray-100 px-3 py-3">
                      <div className="flex items-start justify-between gap-2">
                        <p
                          className="text-[12px] font-bold leading-snug"
                          style={{ color: "#155469" }}
                        >
                          {jobPreview.title}
                        </p>
                        <button
                          type="button"
                          onClick={() => setJobPreview(null)}
                          className="mt-0.5 shrink-0 text-gray-400 hover:text-gray-600"
                        >
                          <X size={12} />
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {[
                          jobPreview.company,
                          jobPreview.location,
                          jobPreview.employmentType,
                          jobPreview.experience,
                          jobPreview.salary !== "Not specified" ? jobPreview.salary : null,
                        ]
                          .filter(Boolean)
                          .map((v) => (
                            <span
                              key={v}
                              className="rounded-full border border-gray-200 bg-white px-2 py-0.5 text-[10px] text-gray-600"
                            >
                              {v}
                            </span>
                          ))}
                      </div>
                      {jobPreview.summary && (
                        <p className="text-[10px] leading-relaxed text-gray-500">
                          {jobPreview.summary}
                        </p>
                      )}
                      {jobPreview.skills && (
                        <p className="text-[10px] text-gray-500">
                          <span className="font-semibold text-gray-700">{t`Skills:`}</span>{" "}
                          {jobPreview.skills}
                        </p>
                      )}
                      {jobPreview.requirements && (
                        <div>
                          <p className="mb-0.5 text-[10px] font-semibold text-gray-700">{t`Requirements:`}</p>
                          <p className="whitespace-pre-line text-[10px] leading-relaxed text-gray-500">
                            {jobPreview.requirements}
                          </p>
                        </div>
                      )}
                      {jobPreview.responsibilities && (
                        <div>
                          <p className="mb-0.5 text-[10px] font-semibold text-gray-700">{t`Responsibilities:`}</p>
                          <p className="whitespace-pre-line text-[10px] leading-relaxed text-gray-500">
                            {jobPreview.responsibilities}
                          </p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="border-t border-gray-100 px-3 py-1.5 text-[10px] text-gray-400">
                      {t`Our AI will read this posting during generation — no extra tokens`}
                    </p>
                  )}
                </div>
              )}

              {/* Description textarea panel */}
              {showJobDescriptionInput && (
                <div className="mt-3 rounded-xl border border-gray-200 bg-gray-50 transition-all focus-within:border-[#268599] focus-within:ring-2 focus-within:ring-[#268599]/10">
                  <textarea
                    value={watch("additionalDescription") || ""}
                    onChange={(e) => setValue("additionalDescription", e.target.value)}
                    placeholder={t`Paste the job description here — our agents will tailor your resume to match it.`}
                    rows={5}
                    className="w-full resize-none rounded-xl bg-transparent px-4 py-3 text-[13px] leading-relaxed text-gray-800 outline-none placeholder:text-gray-400"
                  />
                </div>
              )}
            </div>

            {/* CTA row */}
            <div className="mt-8 flex items-center gap-4 border-t border-gray-100 pt-7">
              <button
                type="button"
                disabled={isLoading}
                onClick={handleButtonClick}
                className="flex items-center gap-2.5 rounded-full px-8 py-4 text-[15px] font-bold text-white transition-all hover:-translate-y-0.5 disabled:opacity-60"
                style={{
                  background:
                    "linear-gradient(135deg,#155469 0%,#268599 25%,#2eb89a 50%,#E8885E 100%)",
                  boxShadow: "0 12px 32px rgba(232,136,94,0.30)",
                }}
              >
                {isLoading ? (
                  <CircleNotch size={18} className="animate-spin" />
                ) : (
                  <Sparkle size={16} weight="fill" />
                )}
                {t`Build my resume`}
              </button>
              {/* Cost meter */}
              <div className="flex-1">
                <div className="mb-1.5 flex justify-between text-[12px] text-gray-400">
                  <span>{t`Simple Steps`}</span>
                  <span>{t`Step 3`}</span>
                </div>
                <div className="h-1.5 overflow-hidden rounded-full bg-gray-100">
                  <div
                    className="h-full rounded-full transition-all"
                    style={{
                      width: `${tokenBalance ? Math.min(100, (STREAMING_COST / tokenBalance) * 100) : 0}%`,
                      background: "linear-gradient(90deg,#155469,#E8885E)",
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Validation error */}
            {errors.jobRole && (
              <p className="mt-3 text-sm text-red-500">{errors.jobRole.message}</p>
            )}
          </div>

          {/* Agent grid */}
          <section className="hidden">
            <div className="mb-4 flex items-baseline justify-between">
              <h2 className="font-serif text-xl font-bold text-gray-900">{t`Your team for this brief`}</h2>
              <p className="text-[12px] text-gray-400">
                <b className="text-[#22c55e]">4 {t`of`} 11</b>{" "}
                {t`agents will collaborate · ~60 sec`}
              </p>
            </div>
            <div className="grid grid-cols-11 gap-2">
              {[
                {
                  key: "architect",
                  label: t`Architect`,
                  img: "/11-agents/new-11-agents/agent_architect.png",
                  active: true,
                },
                {
                  key: "writer",
                  label: t`Writer`,
                  img: "/11-agents/new-11-agents/agent_writer.png",
                  active: true,
                },
                {
                  key: "ats",
                  label: "ATS",
                  img: "/11-agents/new-11-agents/agent_ats.png",
                  active: true,
                },
                {
                  key: "scout",
                  label: t`Scout`,
                  img: "/11-agents/new-11-agents/agent_market_scout.png",
                  active: true,
                },
                {
                  key: "truth",
                  label: t`Truth`,
                  img: "/11-agents/new-11-agents/agent_truth_check.png",
                  active: false,
                },
                {
                  key: "translator",
                  label: t`Translator`,
                  img: "/11-agents/new-11-agents/agent_translator.png",
                  active: false,
                },
                {
                  key: "formatter",
                  label: t`Formatter`,
                  img: "/11-agents/new-11-agents/agent_formatter.png",
                  active: false,
                },
                { key: "editor", label: t`Editor`, img: "", active: false },
                { key: "coach", label: t`Coach`, img: "", active: false },
                { key: "recruiter", label: t`Recruiter`, img: "", active: false },
                { key: "localizer", label: t`Localizer`, img: "", active: false },
              ].map(({ key, label, img, active }) => (
                <div
                  key={key}
                  className={cn(
                    "relative flex flex-col items-center gap-2 rounded-xl border bg-white px-1.5 py-3.5 text-center transition-all",
                    active
                      ? "border-[#268599] bg-gradient-to-b from-white to-[rgba(38,133,153,0.04)]"
                      : "border-gray-100",
                  )}
                >
                  {active && (
                    <span
                      className="absolute right-1.5 top-1.5 size-2 animate-pulse rounded-full bg-[#22c55e]"
                      style={{ boxShadow: "0 0 6px #22c55e" }}
                    />
                  )}
                  {img ? (
                    <img
                      src={img}
                      alt={label}
                      className={cn("h-10 w-10 object-contain", !active && "opacity-40 grayscale")}
                    />
                  ) : (
                    <div
                      className={cn(
                        "flex h-10 w-10 items-center justify-center rounded-full text-[11px] font-bold text-white",
                        active ? "bg-[#268599]" : "bg-gray-200 !text-gray-500",
                      )}
                    >
                      {String(label).charAt(0)}
                    </div>
                  )}
                  <span
                    className={cn(
                      "text-[10px] font-bold leading-tight",
                      active ? "text-[#155469]" : "text-gray-400",
                    )}
                  >
                    {label}
                  </span>
                </div>
              ))}
            </div>
          </section>

          {/* Trust footer */}
          <div className="hidden">
            <div className="flex items-center gap-2.5">
              <div className="flex">
                {(["#268599", "#E8885E", "#DAA579", "#239E9B", "#618480"] as const).map((c, i) => (
                  <span
                    key={i}
                    className="flex size-7 items-center justify-center rounded-full border-2 border-white text-[10px] font-bold text-white"
                    style={{ background: c, marginLeft: i === 0 ? 0 : -8, zIndex: 5 - i }}
                  >
                    {i + 1}
                  </span>
                ))}
              </div>
              <span className="text-[13px] text-gray-600">
                {t`Loved by`} <b className="text-gray-900">6M+</b> {t`professionals`}
              </span>
            </div>
            <div className="flex items-center gap-2 text-[13px] text-gray-600">
              <span className="flex size-[18px] items-center justify-center rounded-full bg-[#22c55e] text-[11px] font-black text-white">
                ✓
              </span>
              <b className="text-gray-900">95%</b> {t`ATS pass rate`}
            </div>
            <div className="flex items-center gap-1.5 text-[13px] text-gray-600">
              <span style={{ color: "#f5b50a" }}>★★★★★</span>
              <b className="text-gray-900">4.4 / 5</b> · 37,389 {t`reviews`}
            </div>
          </div>
        </form>
      )}

      {creationMode === "upload" && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="relative mx-auto w-full max-w-[760px] space-y-6 rounded-[18px] border border-white/[0.08] bg-[#14283e] p-6 shadow-[0_24px_70px_rgba(0,0,0,0.30)] sm:p-8"
        >
          <button
            type="button"
            onClick={() => setCreationMode("form")}
            className="absolute right-4 top-4 z-10 rounded-full p-2 text-white/45 transition-colors hover:bg-white/10 hover:text-white"
            aria-label="Close upload"
          >
            <X size={20} />
          </button>
          <div className="mb-2 pr-10 text-center sm:pr-12">
            <h2 className="mb-2 text-[22px] font-bold text-white">{t`Upload Your Resume`}</h2>
            <p className="text-[13px] text-white/55">
              {t`We'll scan your PDF and extract all the information`}
            </p>
            <p className="mt-2 text-[13px] font-semibold text-[#7fe3f3]">
              {t`Cost:`} {extractPdfCost} {t`tokens`}
            </p>
          </div>

          {/* Upload Dropzone */}
          <div className="relative">
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf"
              className="absolute inset-0 z-10 size-full cursor-pointer opacity-0"
              disabled={isUploading}
              onChange={handlePdfUpload}
            />
            <div
              className={`w-full rounded-[14px] border-2 border-dashed p-8 transition-all sm:p-12 ${
                uploadedFileName
                  ? "border-emerald-400/60 bg-emerald-400/[0.06]"
                  : "border-white/15 bg-[#0c1c2e]/40 hover:border-[#7fe3f3]/45 hover:bg-[#7fe3f3]/[0.04]"
              }`}
            >
              <div className="flex flex-col items-center justify-center gap-4">
                {isUploading ? (
                  <div className="flex flex-col items-center justify-center py-4">
                    {/* Document scanning animation */}
                    <div className="relative mb-6 h-80 w-64">
                      {/* Document shadow */}
                      <div className="absolute inset-0 rotate-2 scale-95 rounded-lg bg-slate-200/50 dark:bg-slate-700/50" />

                      {/* Document */}
                      <div className="relative size-full overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xl dark:border-slate-600 dark:bg-slate-800">
                        {/* Document content placeholder lines */}
                        <div className="space-y-3 p-6">
                          {/* Header placeholder */}
                          <div className="mb-4 flex items-center gap-3">
                            <div className="size-12 rounded-lg bg-blue-100 dark:bg-blue-900/30" />
                            <div className="flex-1 space-y-2">
                              <div className="h-4 w-3/4 rounded bg-blue-200/60 dark:bg-blue-800/40" />
                              <div className="h-3 w-1/2 rounded bg-blue-100/80 dark:bg-blue-900/30" />
                            </div>
                          </div>

                          {/* Content placeholder lines with varying widths */}
                          <div className="h-3 w-full rounded bg-slate-200/80 dark:bg-slate-600/50" />
                          <div className="h-3 w-11/12 rounded bg-slate-200/80 dark:bg-slate-600/50" />
                          <div className="h-3 w-4/5 rounded bg-slate-200/80 dark:bg-slate-600/50" />

                          <div className="space-y-2 pt-3">
                            <div className="h-3 w-1/3 rounded bg-blue-100/80 dark:bg-blue-800/40" />
                            <div className="h-3 w-full rounded bg-slate-200/80 dark:bg-slate-600/50" />
                            <div className="h-3 w-5/6 rounded bg-slate-200/80 dark:bg-slate-600/50" />
                            <div className="h-3 w-11/12 rounded bg-slate-200/80 dark:bg-slate-600/50" />
                          </div>

                          <div className="space-y-2 pt-3">
                            <div className="h-3 w-2/5 rounded bg-blue-100/80 dark:bg-blue-800/40" />
                            <div className="h-3 w-full rounded bg-slate-200/80 dark:bg-slate-600/50" />
                            <div className="h-3 w-3/4 rounded bg-slate-200/80 dark:bg-slate-600/50" />
                          </div>

                          <div className="space-y-2 pt-3">
                            <div className="h-3 w-1/4 rounded bg-blue-100/80 dark:bg-blue-800/40" />
                            <div className="flex gap-2">
                              <div className="h-6 w-16 rounded-full bg-slate-200/80 dark:bg-slate-600/50" />
                              <div className="h-6 w-20 rounded-full bg-slate-200/80 dark:bg-slate-600/50" />
                              <div className="h-6 w-14 rounded-full bg-slate-200/80 dark:bg-slate-600/50" />
                            </div>
                          </div>
                        </div>

                        {/* Animated scan line */}
                        <motion.div
                          className="absolute inset-x-0 h-1 bg-gradient-to-r from-transparent via-blue-500 to-transparent"
                          style={{ boxShadow: "0 0 20px 5px rgba(59, 130, 246, 0.5)" }}
                          initial={{ top: 0 }}
                          animate={{ top: ["0%", "100%", "0%"] }}
                          transition={{
                            duration: 2.5,
                            repeat: Infinity,
                            ease: "linear",
                          }}
                        />
                      </div>
                    </div>

                    {/* Loading text with spinner */}
                    <div className="mb-3 flex items-center gap-3">
                      <motion.div
                        className="size-5 rounded-full border-2 border-blue-500 border-t-transparent"
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                      />
                      <h3 className="text-lg font-semibold text-slate-800 dark:text-white">
                        {t`We're reading your resume...`}
                      </h3>
                    </div>

                    {/* Fact about resumes */}
                    <motion.p
                      key="resume-fact"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="max-w-xs text-center text-sm text-slate-500 dark:text-slate-400"
                    >
                      {t`Fact: recruiters spend less than 7 seconds scanning your resume for the first time.`}
                    </motion.p>
                  </div>
                ) : uploadedFileName ? (
                  <>
                    <div className="rounded-full bg-emerald-100 p-3 dark:bg-emerald-800">
                      <Check
                        className="size-8 text-emerald-600 dark:text-emerald-300"
                        weight="bold"
                      />
                    </div>
                    <div className="text-center">
                      <p className="text-sm font-medium text-emerald-700 dark:text-emerald-300">
                        {uploadedFileName}
                      </p>
                      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{t`Resume uploaded successfully`}</p>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="rounded-full bg-white/[0.06] p-4">
                      <Upload className="size-9 text-white/55" />
                    </div>
                    <div className="text-center">
                      <p className="text-[17px] font-semibold text-white">
                        {t`Drop your PDF here`}
                      </p>
                      <p className="mt-1 text-[13px] text-white/45">{t`or click to browse`}</p>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {uploadError && <p className="text-center text-sm text-red-400">{uploadError}</p>}

          {/* What happens next */}
          <div className="rounded-[12px] border border-white/[0.06] bg-white/[0.03] p-4">
            <p className="text-center text-[12.5px] leading-snug text-white/55">
              {t`After upload, you can refine your resume via AI interview or go directly to the builder`}
            </p>
          </div>
        </motion.div>
      )}

      {/* Post-Upload Modal */}
      <NextStepsModal
        isOpen={showPostUploadModal}
        jobRole={
          uploadedResumeData?.basics?.headline || uploadedResumeData?.basics?.name || "Professional"
        }
        variant="post-extraction"
        onClose={() => {
          setShowPostUploadModal(false);
        }}
        onStartInterview={handleStartInterviewFromUpload}
        onSkipToBuilder={handleSkipToBuilderFromUpload}
      />
    </div>
  );
};
